import React, { createContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductionScheduleDto, DemandDto, ProductionOrderDto } from '../api';
import type { CampaignSequenceEntryDto } from '../api/models/CampaignSequenceEntryDto';
import { SystemSettingControllerService } from '../api/services/SystemSettingControllerService';
import { DemandControllerService } from '../api/services/DemandControllerService';
import { MaterialControllerService } from '../api/services/MaterialControllerService';

import { RoutingControllerService } from '../api/services/RoutingControllerService';
import { ResourceTypeControllerService } from '../api/services/ResourceTypeControllerService';
import { CampaignSequenceControllerService } from '../api/services/CampaignSequenceControllerService';
import { ProductionScheduleControllerService } from '../api/services/ProductionScheduleControllerService';
import { ApiError } from '../api/core/ApiError';
import { useErrorHandler } from '../utils/errorHandler';
import { useToast } from '../hooks/useToast';
import { getEffectiveMaterialId } from '../utils/aliasProductUtils';
import { generateUUID } from '../utils/uuid';
import { timeProvider } from '../services/TimeProvider';
import { isTimeControlAvailable } from '../hooks/useTime';
import {
  calculateBatchDetermination as calculateBatchesFromService,
  resolveEffectiveDoughBatchMethod,
  FixedDoughBatchesValidationError,
} from '../services/batchDeterminationService';
import { rebuildCampaignsWithSplitting } from '../services/campaignRebuildService';
import { getCampaignSplittingFlag } from '../utils/featureFlags';
import { getOvenCapacityCached } from '../services/ovenCapacityService';
import { formatDemandDueDateAsIsoOffset } from '../utils/dueDateIso';
import { logWizardBatchQty } from '../utils/wizardBatchQuantityDebug';
import { persistStep1Demands, savedUuidsOf } from '../services/step1DemandPersistence';

/** Empty grid cell: remove the entry (not the same as an explicit quantity of 0). */
export type Step1DemandCellClear = {
  demandType: 'ORDER' | 'FORECAST';
  materialId: string;
  dateStr: string;
  existingUuid?: string;
};

export type Step1FlushPendingEditsResult = {
  saves: DemandDto[];
  clears: Step1DemandCellClear[];
};

/** Long-term flags (longRunning, longTermOffsetDays) on the main product - demands may sit on the alias UUID. */
function materialForLongTermPlanningRow(
  demand: DemandDto,
  materials: Array<{ uuid?: string; isAlias?: boolean; aliasForProductUuid?: string; longRunning?: boolean; longTermOffsetDays?: number }>
) {
  const effectiveId = getEffectiveMaterialId(demand.materialId, materials) || demand.materialId;
  return materials.find(m => m.uuid === effectiveId) ?? materials.find(m => m.uuid === demand.materialId);
}

// Define the PlanningHorizonSettings type
interface PlanningHorizonSettings {
  defaultDays: number;
  minDays: number;
  maxDays: number;
  allowOverride: boolean;
  /** Maximum number of days into the future that may be chosen as the start of the planning horizon. Default 7. */
  startMaxDaysAhead?: number;
  /** Number of weeks that intermediate product forecasts are projected onto the same weekday. Default 4, 0 = off. */
  intermediateForecastWeeksAhead?: number;
}

// Routing validation issue type
export type RoutingValidationIssue = {
  materialId: string;
  materialName: string;
  routingName?: string;
  reason: 'missingRouting' | 'incompleteRouting' | 'missingProcessingLine';
};

// Legacy BatchData interface (being replaced step by step by the new structure)
export interface BatchData {
  id: string;
  quantity: number;
  weight: number;
  newCampaign: boolean;
  materialId?: string;
  materialName?: string;
  routingId?: string;
  routingName?: string;
  routingFound?: boolean;
  weightKg?: number;
  demandDate?: string; // DateTime string from the demand
}

// New data structures for phase 1: reworked batch determination
// These interfaces match the backend DTOs (CampaignData, DoughBatchData, OvenBatchData)

export interface DoughBatch {
  id?: string;
  uuid?: string; // optional, generated on save
  sequenceNumber: number;
  quantity: number; // piece count in this dough batch
  weight?: number;
  doughQuantityKg: number; // dough quantity in kg (matches doughQuantityKg in the backend)
  materialId?: string;
  materialName?: string;
  // materialId and materialName are no longer here, they live on the campaign
}

export interface OvenBatch {
  uuid?: string; // optional, generated on save
  ovenNumber: number;
  utilization: number; // 0.0 - 1.0 (e.g. 0.7 for 70%)
  pieces: number; // piece count in this oven
  piecesPerOven: number; // oven capacity
  // materialId and materialName are no longer here, they live on the campaign
}

export interface Campaign {
  campaignId: string; // preview in the format yyyyMMdd_<schedulePrefix>_<productSlug>_<seq>, the backend takes it over on save
  sequenceNumber: number; // order (from sequenceEntry.sequenceNumber, matches priority in the old code)
  materialId: string; // material or product id (matches productId in the old code)
  materialName?: string; // material or product name (frontend display only, not stored)
  desiredQuantity?: number; // requested piece count (only in the first campaign, 0 in later ones)
  totalBakedQuantity?: number; // total piece count to bake (after batch determination)
  dueDate?: string; // ISO-8601 with Z or offset (optional)
  doughBatches: DoughBatch[]; // dough batches of this campaign
  ovenBatches?: OvenBatch[]; // oven batches of this campaign
  // sequenceEntryUuid and sequenceEntryIndex are no longer needed
}

export interface BatchDeterminationOverride {
  materialId: string;
  batchDetermination?: 'FULL_OVEN' | 'FULL_STOVE' | 'FULL_CAULDRON' | 'OVEN_UTILIZATION';
  minOvenUtilizationPercent?: number;
  doughBatchMethod?: 'even' | 'full';
}

export interface BatchAssignmentWarning {
  unassignedBatches: number; // number of batches that could not be assigned
  totalBatches: number; // total number of computed batches
  assignedBatches: number; // number of assigned batches
  maxBatchesSum: number | null; // sum of maxBatches across all sequence entries (null = unlimited)
}

/** Time-related demand bucket: quantity for one demand date only (alias plus main product on the same date). */
export interface DemandDateBucket {
  demandDate: string; // ISO date or date-time
  quantity: number;
  dueTime?: string;
}

export interface WizardBatchData {
  materialId: string;
  materialName: string;
  batchDetermination: 'FULL_OVEN' | 'FULL_STOVE' | 'FULL_CAULDRON' | 'OVEN_UTILIZATION';
  // Phase 1: only FULL_OVEN and OVEN_UTILIZATION are used
  // FULL_STOVE and FULL_CAULDRON exist in the enum but are not visible in the UI
  minOvenUtilizationPercent?: number; // only with OVEN_UTILIZATION
  desiredQuantity: number; // requested piece count (editable by hand)
  bakedQuantity: number; // piece count to bake (computed)
  doughQuantity: number; // dough quantity in kg (computed)
  doughBatches: DoughBatch[];
  ovenBatches?: OvenBatch[]; // only with OVEN_UTILIZATION (but the grid is shown for FULL_OVEN as well)
  campaigns: Campaign[]; // assignment to campaigns
  doughBatchMethod?: 'even' | 'full'; // method used to form the dough batches
  assignmentWarning?: BatchAssignmentWarning; // warning when not all batches could be assigned
  ovenCapacityWarnings?: Array<{ campaignId: string; ovenBatches: number; availableOvens: number }>; // campaigns with more oven batches than there are ovens available
}

export interface WizardState {
  // Form data
  formData: Partial<ProductionScheduleDto>;
  
  // Planning settings
  planningSettings: PlanningHorizonSettings;
  isLoadingSettings: boolean;
  
  // Demands and materials
  demands: DemandDto[];
  /** Orders (demandType ORDER) - for the orders tab */
  orders: DemandDto[];
  /** Forecasts (demandType FORECAST) - for the forecasts tab */
  forecasts: DemandDto[];
  /** Merged result: for each (materialId, demandDate), order wins over forecast. Used for downstream steps. */
  effectiveDemands: DemandDto[];
  /** For the production quantities tab: effectiveDemands with demandDate shifted back by longTermOffsetDays for long-running products (production date D shows the demand for D+offset). */
  productionEffectiveDemands: DemandDto[];
  /** For the production quantities tab: merged demands for the DISPLAY WEEK (7 days), with the long-term offset. All entries are mirrored, not just the planning horizon. */
  productionDisplayDemands: DemandDto[];
  /** For the production quantities table: aggregated by (effectiveId, demandDate) - one row per main product, cells = sum of alias and main on the same date. */
  productionDisplayDemandsAggregated: DemandDto[];
  /** Per main product (effectiveId): demand buckets per date within the planning horizon. Quantity = sum of alias and main on the same date only. For time-related batches and due dates. */
  demandBucketsByProduct: Record<string, DemandDateBucket[]>;
  /** Manually added orders (not yet saved) - persisted when switching to Step 2 */
  pendingOrders: DemandDto[];
  /** Manually added forecasts (not yet saved) - persisted when switching to Step 2 */
  pendingForecasts: DemandDto[];
  /** UUIDs of persisted demands to delete when user set quantity to 0 */
  pendingDeletions: string[];
  materials: any[];
  isLoadingDemands: boolean;
  
  // Batch calculation
  selectedMaterialIndex: number;
  batches: BatchData[];
  localBatchesMap: Record<string, BatchData[]>;
  
  // New batch data structure (phase 1)
  batchDeterminationData: Record<string, WizardBatchData>; // key: materialId
  batchDeterminationOverrides: Record<string, BatchDeterminationOverride>; // key: materialId
  
  // Production Orders
  productionOrders: ProductionOrderDto[];
  isCreatingProductionOrders: boolean;
  productionOrderCreationProgress: number;
  
  // Validation state
  step3ValidationError: { batchesWithoutRouting: BatchData[] } | null;
  routingValidationIssues: RoutingValidationIssue[] | null;
  /** Material IDs for which quantity is locked to 0 (no work plan) */
  materialIdsQuantityLocked: Record<string, boolean>;
  
  // Campaign Sequence (Step 2)
  sequenceOptimization: boolean; // true = automatic sequence optimization (no manual sequence)
  selectedDayOfWeek: string | null; // MONDAY, TUESDAY, etc.
  selectedSequenceUuid: string | null; // UUID of the sequence chosen in the wizard (the default or another one)
  availableSequencesForDay: Array<{ uuid?: string; name?: string; isDefault?: boolean }>; // list for the dropdown
  sequenceEntries: CampaignSequenceEntryDto[];
  autoAddedProductIds: Set<string>; // Track which products were auto-added from demands
  
  // Step 0: planning mode (AP0 - replanning and follow-on planning)
  planningMode: 'NEW_PLANNING' | 'CONTINUATION' | 'REPLANNING';
  sourceScheduleUuid: string | null; // on replanning: the source plan
  sourceSchedulePlannedStart: string | null; // on replanning: plannedStart of the source plan (= the canonical order date)
  frozenZoneEnd: string | null; // on replanning: end of the frozen zone (ISO DateTime)
  /** On replanning: fixed dough batches per material (started inside the frozen zone). */
  fixedDoughBatchesByMaterial: Record<string, DoughBatch[]>;
  /** Step 0: loads the list of released and active plans (for follow-on planning and replanning). */
  step0ReleasedPlansLoading: boolean;
  /** Step 0: at least one released or active plan exists. */
  step0HasReleasedPlans: boolean;
  /** Step 1, replanning: true when at least one row has "new target < actual" - block Next. */
  step1ReplanningHasNewSollBelowIst: boolean;
  /** Replanning: material UUIDs whose first operation is already finished (locked quantities). */
  lockedReplanningMaterialIds: Set<string>;

  /** Long-term follow-up orders (finish baking) for the current plan window - from the preview API. */
  hasFollowUpOrders: boolean;
  /** List of follow-up batches for the finish-baking tab (same shape as new batches: sequenceNumber, productName, campaignId, batchSequenceNumber, quantity, doughQuantity). */
  followUpOrders: Array<{ sequenceNumber?: number; productName?: string; campaignId?: string; batchSequenceNumber?: number; quantity?: number; doughQuantity?: number }>;

  // UI state
  currentStep: number;
  settingsLoadedRef: React.MutableRefObject<boolean>;
}

export interface WizardActions {
  // Form actions
  updateFormData: (field: keyof ProductionScheduleDto, value: any) => void;
  
  // Step navigation
  setCurrentStep: (step: number) => void;
  handleNext: () => void;
  handlePrevious: () => void;
  
  // Batch actions (legacy - being replaced step by step)
  setSelectedMaterialIndex: (index: number) => void;
  setBatches: (batches: BatchData[]) => void;
  updateBatch: (batchId: string, updates: Partial<BatchData>) => void;
  updateBatchesForMaterial: (materialId: string, batches: BatchData[]) => void;
  setLocalBatchesMap: (map: Record<string, BatchData[]>) => void;
  
  // New batch actions (phase 1)
  setBatchDeterminationData: (materialId: string, data: WizardBatchData) => void;
  setBatchDeterminationOverride: (materialId: string, override: BatchDeterminationOverride) => void;
  /** desiredQuantityOrDateBuckets: number = one total quantity (legacy); DemandDateBucket[] = time-related buckets (quantity per date, dueDate per bucket). */
  calculateBatchDetermination: (materialId: string, desiredQuantityOrDateBuckets: number | DemandDateBucket[], doughBatchMethod?: 'even' | 'full', fixedDoughBatches?: DoughBatch[]) => void;
  adjustBatchCountForSequence: (materialId: string) => void; // Automatic adjustment of the batch count
  removeBatchDeterminationData: (materialIds: string[]) => void;
  /** Set campaigns without work plan to quantity 0 and lock fields (instead of removing) */
  setCampaignsWithoutRoutingToZero: (materialIds: string[]) => void;
  
  // Data loading
  loadDemands: (startDate: string, endDate: string) => Promise<void>;
  loadMaterials: () => Promise<void>;

  // Step 1: Pending demands (inline grid edit + CSV import, saved on Next)
  addOrUpdatePendingOrder: (demand: DemandDto, existingUuid?: string) => void;
  addOrUpdatePendingForecast: (demand: DemandDto, existingUuid?: string) => void;
  /** Clear an order or forecast cell (no record) - different from a quantity of 0. */
  clearPendingDemandCell: (clear: Step1DemandCellClear) => void;
  /** CSV import: add several demands to pending (same logic as manual entry) */
  addPendingOrdersFromImport: (demands: DemandDto[]) => void;
  addPendingForecastsFromImport: (demands: DemandDto[]) => void;
  addPendingDeletion: (demandUuid: string) => void;
  savePendingStep1Demands: (flush?: Step1FlushPendingEditsResult | DemandDto[]) => Promise<DemandDto[] | undefined>;
  
  // Production Order actions
  createProductionOrders: () => Promise<{ success: boolean; error?: string }>;
  deleteProductionOrders: () => Promise<void>;
  validateStep3: () => { isValid: boolean; batchesWithoutRouting: BatchData[] };
  clearStep3ValidationError: () => void;
  
  // Campaign Sequence actions (Step 2)
  setSequenceOptimization: (value: boolean) => void;
  setSelectedDayOfWeek: (dayOfWeek: string) => void;
  setSelectedSequenceUuid: (uuid: string | null) => void;
  setAvailableSequencesForDay: (list: Array<{ uuid?: string; name?: string; isDefault?: boolean }>) => void;
  setSequenceEntries: React.Dispatch<React.SetStateAction<CampaignSequenceEntryDto[]>>;
  addSequenceEntry: (entry: CampaignSequenceEntryDto) => void;
  updateSequenceEntry: (uuid: string, updates: Partial<CampaignSequenceEntryDto>) => void;
  deleteSequenceEntry: (uuid: string) => void;
  setAutoAddedProductIds: (ids: Set<string>) => void;
  validateSequenceEntries: () => { isValid: boolean; errors: string[] };
  /** Returns the preview schedule UUID used for campaign ID preview (or null if not yet set). */
  getPreviewScheduleUuid: () => string | null;

  // Step 0: Planning mode
  setPlanningMode: (mode: 'NEW_PLANNING' | 'CONTINUATION' | 'REPLANNING') => void;
  setSourceScheduleUuid: (uuid: string | null) => void;
  setSourceSchedulePlannedStart: (value: string | null) => void;
  setFrozenZoneEnd: (value: string | null) => void;
  /** Step 0: called by Step0PlanningMode once the list of released plans has loaded. */
  setStep0ReleasedPlansState: (loading: boolean, count: number) => void;
  setStep1ReplanningHasNewSollBelowIst: (value: boolean) => void;
  setLockedReplanningMaterialIds: (ids: Set<string>) => void;

  // Utility functions
  generateDefaultScheduleName: () => string;
  convertDemandsForView: (demands: DemandDto[]) => any[];
  formatDate: (dateString: string) => string;
  calculateBatches: (totalQuantity: number, maxBatchSizeKg: number, minBatchSizeKg: number, method: string, initialWeightGrams: number) => void;

  /** Direct start from step 1: registers the save function for "start planning" with defaults. */
  registerDirectStartSave: (fn: (() => Promise<void>) | null) => void;
  /** Prepares the defaults and starts the planning (calls the registered save function). */
  startPlanningWithDefaults: () => Promise<boolean>;

  /** Step 1: registers a flush function that returns the local grid edits (called before changing step). */
  registerStep1FlushPendingEdits: (fn: (() => Step1FlushPendingEditsResult) | null) => void;

  /**
   * Step 2a (intermediate products): registers the flush and cleanup functions.
   * flush is called before step 4 -> 5 and should send the manual intermediate product orders to the backend.
   * Return value true = OK, the wizard may continue; false = blocked (the step stays active).
   * cleanup is called before step 4 -> 3 and should delete the intermediate product orders already created for this schedule.
   */
  registerStep2aIntermediateFlush: (fn: (() => Promise<boolean>) | null) => void;
  registerStep2aIntermediateCleanup: (fn: (() => Promise<void>) | null) => void;
  pendingIntermediateOrders: Array<{ intermediateProductUuid: string; lotSize: number; plannedStart?: string; plannedEnd?: string; orderNumber?: string }>;
  setPendingIntermediateOrders: (orders: Array<{ intermediateProductUuid: string; lotSize: number; plannedStart?: string; plannedEnd?: string; orderNumber?: string }>) => void;
}

export const WizardContext = createContext<WizardState & WizardActions | undefined>(undefined);

interface WizardProviderProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

export const WizardProvider: React.FC<WizardProviderProps> = ({
  children,
  isOpen,
  onClose: _onClose
}) => {
  const { t, i18n } = useTranslation(['common']);
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();
  
  // Track if this is the first mount to detect hot reloads
  const isFirstMountRef = useRef(true);
  const [_forceUpdate, setForceUpdate] = useState(0);
  const demandsLoadedRef = useRef(false);
  const lastLoadedDateRangeRef = useRef<string | null>(null);
  
  // Mark as not first mount after initial render
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false;
    } else {
      // This is a hot reload - force a re-render to update context
      if (process.env.NODE_ENV === 'development') {
        // Hot reload detected, forcing context update
        // Force update by triggering a state change
        setForceUpdate(prev => prev + 1);
        // Reset demands loaded flag on hot reload
        demandsLoadedRef.current = false;
      }
    }
  }, []);

  // Fetch campaign-splitting feature flag once (tenant-scoped system setting).
  useEffect(() => {
    getCampaignSplittingFlag()
      .then(res => { campaignSplittingEnabledRef.current = res.enabled !== false; })
      .catch(() => { campaignSplittingEnabledRef.current = true; }); // fail-safe: keep default behavior
  }, []);
  
  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [planningMode, setPlanningModeState] = useState<'NEW_PLANNING' | 'CONTINUATION' | 'REPLANNING'>('NEW_PLANNING');
  const [sourceScheduleUuid, setSourceScheduleUuid] = useState<string | null>(null);
  const [sourceSchedulePlannedStart, setSourceSchedulePlannedStart] = useState<string | null>(null);
  const [frozenZoneEnd, setFrozenZoneEnd] = useState<string | null>(null);
  const [fixedDoughBatchesByMaterial, setFixedDoughBatchesByMaterial] = useState<Record<string, DoughBatch[]>>({});
  const [step0ReleasedPlansLoading, setStep0ReleasedPlansLoading] = useState(true);
  const [step0HasReleasedPlans, setStep0HasReleasedPlans] = useState(false);
  const [step1ReplanningHasNewSollBelowIst, setStep1ReplanningHasNewSollBelowIst] = useState(false);
  const [lockedReplanningMaterialIds, setLockedReplanningMaterialIds] = useState<Set<string>>(new Set());
  const setStep0ReleasedPlansState = useCallback((loading: boolean, count: number) => {
    setStep0ReleasedPlansLoading(loading);
    setStep0HasReleasedPlans(count > 0);
  }, []);
  const [planningSettings, setPlanningSettings] = useState<PlanningHorizonSettings>({
    defaultDays: 1,
    allowOverride: true,
    minDays: 1,
    maxDays: 1,
    startMaxDaysAhead: 7,
    intermediateForecastWeeksAhead: 4
  });
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [demands, setDemands] = useState<DemandDto[]>([]);
  const [pendingOrders, setPendingOrders] = useState<DemandDto[]>([]);
  const [pendingForecasts, setPendingForecasts] = useState<DemandDto[]>([]);
  const [pendingDeletions, setPendingDeletions] = useState<string[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [isLoadingDemands, setIsLoadingDemands] = useState(false);
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState(0);
  const [batches, setBatches] = useState<BatchData[]>([]);
  const [localBatchesMap, setLocalBatchesMap] = useState<Record<string, BatchData[]>>({});
  
  // New batch data structure (phase 1)
  const [batchDeterminationData, setBatchDeterminationDataState] = useState<Record<string, WizardBatchData>>({});
  const [batchDeterminationOverrides, setBatchDeterminationOverrides] = useState<Record<string, BatchDeterminationOverride>>({});
  
  const [productionOrders, setProductionOrders] = useState<ProductionOrderDto[]>([]);
  const [isCreatingProductionOrders, setIsCreatingProductionOrders] = useState(false);

  // Manual intermediate product orders from step 2a, buffered until the final save.
  // Schedule, campaigns and intermediate product orders are created in one transaction at the end.
  const [pendingIntermediateOrders, setPendingIntermediateOrders] = useState<Array<{
    intermediateProductUuid: string;
    lotSize: number;
    plannedStart?: string;
    plannedEnd?: string;
    orderNumber?: string;
  }>>([]);
  const [productionOrderCreationProgress, setProductionOrderCreationProgress] = useState(0);
  const [step3ValidationError, setStep3ValidationError] = useState<{ batchesWithoutRouting: BatchData[] } | null>(null);
  const [routingValidationIssues, setRoutingValidationIssues] = useState<RoutingValidationIssue[] | null>(null);
  const [materialIdsQuantityLocked, setMaterialIdsQuantityLocked] = useState<Record<string, boolean>>({});
  /** True if there are long-term follow-up orders (finish baking) for the current plan window - allows step 3 Next without batches. */
  const [hasFollowUpOrders, setHasFollowUpOrders] = useState<boolean>(false);
  /** List for the finish-baking tab - set from the preview API response. */
  const [followUpOrders, setFollowUpOrders] = useState<Array<{ sequenceNumber?: number; productName?: string; campaignId?: string; batchSequenceNumber?: number; quantity?: number; doughQuantity?: number }>>([]);
  
  // Campaign Sequence state (Step 2)
  const [sequenceOptimization, setSequenceOptimization] = useState<boolean>(false);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState<string | null>(null);
  const [selectedSequenceUuid, setSelectedSequenceUuid] = useState<string | null>(null);
  const [availableSequencesForDay, setAvailableSequencesForDay] = useState<Array<{ uuid?: string; name?: string; isDefault?: boolean }>>([]);
  const [sequenceEntries, setSequenceEntries] = useState<CampaignSequenceEntryDto[]>([]);
  const [autoAddedProductIds, setAutoAddedProductIds] = useState<Set<string>>(new Set());
  
  const setPlanningMode = useCallback((mode: 'NEW_PLANNING' | 'CONTINUATION' | 'REPLANNING') => {
    setPlanningModeState(mode);
    if (mode !== 'REPLANNING') {
      setSourceScheduleUuid(null);
      setSourceSchedulePlannedStart(null);
      setFrozenZoneEnd(null);
      setFixedDoughBatchesByMaterial({});
      setStep1ReplanningHasNewSollBelowIst(false);
      setLockedReplanningMaterialIds(new Set());
    }
  }, []);

  const settingsLoadedRef = useRef(false);
  const materialsLoadAttemptedRef = useRef(false);
  /** UUID used for campaign ID preview; set on first batch determination, passed to backend on save. */
  const previewScheduleUuidRef = useRef<string | null>(null);
  /**
   * Cache for the oven capacity per material (how many ovens of the required type are available in parallel).
   * Prefetched when the materials load; `calculateBatchDetermination` reads from this ref synchronously.
   * When no value is available yet, the split is skipped (best effort).
   */
  const availableOvensByMaterialRef = useRef<Record<string, number>>({});
  /** Feature flag: when false, campaigns are not split automatically. Default true. */
  const campaignSplittingEnabledRef = useRef<boolean>(true);
  /** For the direct start: the save function registered by ProductionScheduleWizardContent. */
  const directStartSaveRef = useRef<(() => Promise<void>) | null>(null);

  // The planning horizon always begins on the next day (day level, 00:00 local time).
  const getTomorrowStartLocal = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const y = tomorrow.getFullYear();
    const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const d = String(tomorrow.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}T00:00:00`;
  };
  const getTomorrowEndLocal = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 999);
    const y = tomorrow.getFullYear();
    const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const d = String(tomorrow.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}T23:59:59`;
  };

  const [formData, setFormData] = useState<Partial<ProductionScheduleDto>>({
    plannedStart: getTomorrowStartLocal(),
    plannedEnd: getTomorrowEndLocal(),
    isReleased: false
  });

  // In DEV and on stage: set the planning time to the server time (the mock time).
  // Important: with maxDays === 1, plannedEnd has to be the same day as plannedStart (23:59:59), otherwise several buckets appear.
  useEffect(() => {
    if (!isTimeControlAvailable) return;
    let cancelled = false;
    timeProvider.getCurrentTime().then((serverNow) => {
      if (cancelled) return;
      const tomorrow = new Date(serverNow);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      const maxDays = planningSettings.maxDays ?? 1;
      const end = maxDays === 1
        ? new Date(tomorrow.getTime())
        : new Date(tomorrow.getTime() + (maxDays - 1) * 24 * 60 * 60 * 1000);
      end.setHours(23, 59, 59, 999);
      const fmt = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        const s = String(d.getSeconds()).padStart(2, '0');
        return `${y}-${m}-${day}T${h}:${min}:${s}`;
      };
      setFormData((prev) => ({
        ...prev,
        plannedStart: fmt(tomorrow),
        plannedEnd: fmt(end),
      }));
    }).catch(() => { /* Fallback: the initial values (browser time) stay */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isTimeControlAvailable is an outer scope mutable, not valid as dependency
  }, [planningSettings.maxDays]);

  // Load the finish-baking preview when the plan window is set, or when entering step 3 (avoids a stale "no finish baking" after changing the horizon)
  useEffect(() => {
    const start = formData.plannedStart;
    const end = formData.plannedEnd;
    if (!start || !end) {
      setHasFollowUpOrders(false);
      return;
    }
    let cancelled = false;
    ProductionScheduleControllerService.getLongTermFollowUpOrdersPreview(start, end)
      .then((res) => {
        if (cancelled) return;
        setHasFollowUpOrders(!!res.hasFollowUpOrders);
        setFollowUpOrders(Array.isArray(res.followUpOrders) ? res.followUpOrders : []);
      })
      .catch(() => {
        if (!cancelled) {
          setHasFollowUpOrders(false);
          // Do not clear followUpOrders on an error - keep the data we have
        }
      });
    return () => { cancelled = true; };
  }, [formData.plannedStart, formData.plannedEnd, currentStep]);

  // Load materials - use useCallback to ensure stable reference
  const loadMaterials = useCallback(async () => {
    try {
      const materialsData = await MaterialControllerService.findAll();
      setMaterials(materialsData);
    } catch (error) {
      handleError(error, 'loading materials');
      setMaterials([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleError may not be stable; loads only once
  }, []);

  // Prefetch available oven capacity per material.
  // Populates `availableOvensByMaterialRef` so `calculateBatchDetermination` can
  // split campaigns synchronously. Materials without a routing or oven operation
  // are silently skipped (backend returns 400; we treat them as "no split needed").
  useEffect(() => {
    if (!materials || materials.length === 0) return;
    let cancelled = false;
    for (const m of materials) {
      if (!m.uuid || availableOvensByMaterialRef.current[m.uuid] !== undefined) continue;
      getOvenCapacityCached(m.uuid)
        .then(cap => {
          if (cancelled) return;
          availableOvensByMaterialRef.current[m.uuid!] = cap.availableOvens ?? 0;
        })
        .catch(() => { /* no routing / no oven — skip split for this material */ });
    }
    return () => { cancelled = true; };
  }, [materials]);

  // Calculate batches based on selected method
  const calculateBatches = useCallback((totalQuantity: number, maxBatchSizeKg: number, minBatchSizeKg: number, method: string, initialWeightGrams: number) => {
    const calculatedBatches: BatchData[] = [];
    
    // Convert total quantity to total weight in kg
    const totalWeightKg = (totalQuantity * initialWeightGrams) / 1000;
    
    if (method === 'even') {
      // Even distribution - calculate number of batches based on weight
      const batchCount = Math.ceil(totalWeightKg / maxBatchSizeKg);
      const weightPerBatchKg = totalWeightKg / batchCount;
      const piecesPerBatch = Math.round((weightPerBatchKg * 1000) / initialWeightGrams);
      
      for (let i = 0; i < batchCount; i++) {
        const actualWeight = piecesPerBatch * initialWeightGrams;
        calculatedBatches.push({
          id: (i + 1).toString(),
          quantity: piecesPerBatch,
          weight: actualWeight,
          newCampaign: false,
          materialId: '',
          materialName: ''
        });
      }
    } else if (method === 'full') {
      // Full quantities - fill to max size, remainder in last batch
      const fullBatches = Math.floor(totalWeightKg / maxBatchSizeKg);
      const remainderWeightKg = totalWeightKg % maxBatchSizeKg;
      
      if (remainderWeightKg > 0 && remainderWeightKg < minBatchSizeKg) {
        const neededWeight = minBatchSizeKg - remainderWeightKg;
        const weightPerBatchToReduce = neededWeight / fullBatches;
        
        if (weightPerBatchToReduce > maxBatchSizeKg) {
          const newFullBatches = fullBatches + 1;
          const newRemainder = totalWeightKg - (newFullBatches * maxBatchSizeKg);
          
          for (let i = 0; i < newFullBatches; i++) {
            const pieces = Math.round((maxBatchSizeKg * 1000) / initialWeightGrams);
            calculatedBatches.push({
              id: (i + 1).toString(),
              quantity: pieces,
              weight: pieces * initialWeightGrams,
              newCampaign: false,
              materialId: '',
              materialName: ''
            });
          }
          
          if (newRemainder > 0) {
            const remainderPieces = Math.round((newRemainder * 1000) / initialWeightGrams);
            calculatedBatches.push({
              id: (newFullBatches + 1).toString(),
              quantity: remainderPieces,
              weight: remainderPieces * initialWeightGrams,
              newCampaign: false,
              materialId: '',
              materialName: ''
            });
          }
        } else {
          for (let i = 0; i < fullBatches; i++) {
            const adjustedWeight = maxBatchSizeKg - weightPerBatchToReduce;
            const pieces = Math.round((adjustedWeight * 1000) / initialWeightGrams);
            calculatedBatches.push({
              id: (i + 1).toString(),
              quantity: pieces,
              weight: pieces * initialWeightGrams,
              newCampaign: false,
              materialId: '',
              materialName: ''
            });
          }
          
          const remainderPieces = Math.round((minBatchSizeKg * 1000) / initialWeightGrams);
          calculatedBatches.push({
            id: (fullBatches + 1).toString(),
            quantity: remainderPieces,
            weight: remainderPieces * initialWeightGrams,
            newCampaign: false,
            materialId: '',
            materialName: ''
          });
        }
      } else {
        for (let i = 0; i < fullBatches; i++) {
          const pieces = Math.round((maxBatchSizeKg * 1000) / initialWeightGrams);
          calculatedBatches.push({
            id: (i + 1).toString(),
            quantity: pieces,
            weight: pieces * initialWeightGrams,
            newCampaign: false,
            materialId: '',
            materialName: ''
          });
        }
        
        if (remainderWeightKg > 0) {
          const remainderPieces = Math.round((remainderWeightKg * 1000) / initialWeightGrams);
          calculatedBatches.push({
            id: (fullBatches + 1).toString(),
            quantity: remainderPieces,
            weight: remainderPieces * initialWeightGrams,
            newCampaign: false,
            materialId: '',
            materialName: ''
          });
        }
      }
    }
    
    setBatches(calculatedBatches);
  }, []);

  // Load open demands based on current date range
  const loadDemands = useCallback(async (startDate: string, endDate: string) => {
    try {
      setIsLoadingDemands(true);
      const demandsData = await DemandControllerService.getOpenDemands(startDate, endDate);
      setDemands(demandsData);
      demandsLoadedRef.current = true;
    } catch (error) {
      handleError(error, 'loading open demands');
      setDemands([]);
      demandsLoadedRef.current = true;
    } finally {
      setIsLoadingDemands(false);
    }
  }, [handleError]);

  // Replanning: load the fixed dough batches (started inside the frozen zone)
  useEffect(() => {
    if (planningMode !== 'REPLANNING' || !sourceScheduleUuid || !frozenZoneEnd) {
      setFixedDoughBatchesByMaterial({});
      return;
    }
    let cancelled = false;
    const effectiveAt = frozenZoneEnd.replace('Z', '').trim();
    ProductionScheduleControllerService.getFixedDoughBatchesUntil(sourceScheduleUuid, effectiveAt)
      .then((byMaterial) => {
        if (cancelled || !byMaterial) return;
        const mapped: Record<string, DoughBatch[]> = {};
        for (const [matId, batches] of Object.entries(byMaterial)) {
          if (!batches?.length) continue;
          // Defensive reindex to 1, 2, 3, ... regardless of what the backend sends.
          // validateFixedDoughBatches in batchDeterminationService rejects gaps and offsets
          // (e.g. seq=4 alone -> "expected 1, got 4"). Reindexing here keeps the
          // contract local to the wizard - downstream code only ever sees clean 1..N.
          mapped[matId] = batches.map((b, i) => ({
            uuid: b.uuid,
            sequenceNumber: i + 1,
            quantity: b.quantity ?? 0,
            doughQuantityKg: b.doughQuantityKg ?? 0
          }));
        }
        setFixedDoughBatchesByMaterial(mapped);
      })
      .catch(() => {
        if (!cancelled) setFixedDoughBatchesByMaterial({});
      });
    return () => { cancelled = true; };
  }, [planningMode, sourceScheduleUuid, frozenZoneEnd]);

  const addPendingDeletion = useCallback((demandUuid: string) => {
    setPendingDeletions(prev => (prev.includes(demandUuid) ? prev : [...prev, demandUuid]));
  }, []);

  const addOrUpdatePendingOrder = useCallback((demand: DemandDto, existingUuid?: string) => {
    const dateStr = demand.demandDate?.split('T')[0] || demand.demandDate || '';
    const q = demand.quantity;
    if (q === 0) {
      // An explicit 0 overrides the forecast with a demand of zero. It updates the existing row
      // rather than deleting it and creating a new one: the delete-and-create path reset the due
      // time to the default, so the same user action produced two different results depending on
      // whether a tab switch routed it through here or through flushLocalEdits, which keeps the
      // uuid (review round 5, finding 1.1).
      if (existingUuid) {
        setPendingDeletions(prev => prev.filter(u => u !== existingUuid));
      }
      setPendingOrders(prev => {
        const filtered = prev.filter(
          d => !(d.materialId === demand.materialId && (d.demandDate?.split('T')[0] || d.demandDate) === dateStr)
        );
        const newD = { ...demand, demandType: 'ORDER' as const, quantity: 0 };
        return [...filtered, newD];
      });
      return;
    }
    if (q != null && q > 0) {
      if (existingUuid) {
        setPendingDeletions(prev => prev.filter(u => u !== existingUuid));
      }
      setPendingOrders(prev => {
        const filtered = prev.filter(
          d => !(d.materialId === demand.materialId && (d.demandDate?.split('T')[0] || d.demandDate) === dateStr)
        );
        const newD = { ...demand, demandType: 'ORDER' as const };
        return [...filtered, newD];
      });
      return;
    }
    if (existingUuid) {
      addPendingDeletion(existingUuid);
    }
    setPendingOrders(prev =>
      prev.filter(d => !(d.materialId === demand.materialId && (d.demandDate?.split('T')[0] || d.demandDate) === dateStr))
    );
  }, [addPendingDeletion]);

  const addOrUpdatePendingForecast = useCallback((demand: DemandDto, existingUuid?: string) => {
    const dateStr = demand.demandDate?.split('T')[0] || demand.demandDate || '';
    const q = demand.quantity;
    if (q === 0) {
      // An explicit 0 overrides the forecast with a demand of zero. It updates the existing row
      // rather than deleting it and creating a new one: the delete-and-create path reset the due
      // time to the default, so the same user action produced two different results depending on
      // whether a tab switch routed it through here or through flushLocalEdits, which keeps the
      // uuid (review round 5, finding 1.1).
      if (existingUuid) {
        setPendingDeletions(prev => prev.filter(u => u !== existingUuid));
      }
      setPendingForecasts(prev => {
        const filtered = prev.filter(
          d => !(d.materialId === demand.materialId && (d.demandDate?.split('T')[0] || d.demandDate) === dateStr)
        );
        const newD = { ...demand, demandType: 'FORECAST' as const, quantity: 0 };
        return [...filtered, newD];
      });
      return;
    }
    if (q != null && q > 0) {
      if (existingUuid) {
        setPendingDeletions(prev => prev.filter(u => u !== existingUuid));
      }
      setPendingForecasts(prev => {
        const filtered = prev.filter(
          d => !(d.materialId === demand.materialId && (d.demandDate?.split('T')[0] || d.demandDate) === dateStr)
        );
        const newD = { ...demand, demandType: 'FORECAST' as const };
        return [...filtered, newD];
      });
      return;
    }
    if (existingUuid) {
      addPendingDeletion(existingUuid);
    }
    setPendingForecasts(prev =>
      prev.filter(d => !(d.materialId === demand.materialId && (d.demandDate?.split('T')[0] || d.demandDate) === dateStr))
    );
  }, [addPendingDeletion]);

  const clearPendingDemandCell = useCallback((c: Step1DemandCellClear) => {
    const dateStr = c.dateStr;
    if (c.existingUuid) {
      addPendingDeletion(c.existingUuid);
    }
    if (c.demandType === 'ORDER') {
      setPendingOrders(prev =>
        prev.filter(d => !(d.materialId === c.materialId && (d.demandDate?.split('T')[0] || d.demandDate) === dateStr))
      );
    } else {
      setPendingForecasts(prev =>
        prev.filter(d => !(d.materialId === c.materialId && (d.demandDate?.split('T')[0] || d.demandDate) === dateStr))
      );
    }
  }, [addPendingDeletion]);

  const keyForDemand = (d: DemandDto) =>
    `${d.materialId || ''}|${d.demandDate?.split('T')[0] || d.demandDate || ''}|${d.dueTime || ''}`;

  const addPendingOrdersFromImport = useCallback((demands: DemandDto[]) => {
    if (demands.length === 0) return;
    const normalized = demands
      .filter(d => d.materialId)
      .map(d => ({
        ...d,
        demandType: 'ORDER' as const,
        demandDate: d.demandDate?.includes('T') ? d.demandDate : `${d.demandDate?.split('T')[0] || d.demandDate}T00:00:00`
      }));
    setPendingOrders(prev => {
      const byKey = new Map(prev.map(d => [keyForDemand(d), d]));
      normalized.forEach(d => byKey.set(keyForDemand(d), d));
      return Array.from(byKey.values());
    });
  }, []);

  const addPendingForecastsFromImport = useCallback((demands: DemandDto[]) => {
    if (demands.length === 0) return;
    const normalized = demands
      .filter(d => d.materialId)
      .map(d => ({
        ...d,
        demandType: 'FORECAST' as const,
        demandDate: d.demandDate?.includes('T') ? d.demandDate : `${d.demandDate?.split('T')[0] || d.demandDate}T00:00:00`
      }));
    setPendingForecasts(prev => {
      const byKey = new Map(prev.map(d => [keyForDemand(d), d]));
      normalized.forEach(d => byKey.set(keyForDemand(d), d));
      return Array.from(byKey.values());
    });
  }, []);

  const savePendingStep1Demands = useCallback(async (flush: Step1FlushPendingEditsResult | DemandDto[] = { saves: [], clears: [] }): Promise<DemandDto[] | undefined> => {
    const saves = Array.isArray(flush) ? flush : flush.saves;
    const clears = Array.isArray(flush) ? [] : (flush.clears ?? []);
    const toDelete = [...pendingDeletions];
    const dedupeKey = (d: DemandDto) => `${d.materialId}|${d.demandDate?.split('T')[0] || ''}|${d.demandType || 'ORDER'}`;
    const clearKey = (c: Step1DemandCellClear) => `${c.materialId}|${c.dateStr}|${c.demandType}`;
    const merged = new Map<string, DemandDto>();
    [...pendingOrders, ...pendingForecasts].forEach(d => merged.set(dedupeKey(d), d));
    clears.forEach(c => {
      merged.delete(clearKey(c));
      if (c.existingUuid && !toDelete.includes(c.existingUuid)) {
        toDelete.push(c.existingUuid);
      }
    });
    saves.forEach(d => merged.set(dedupeKey(d), d));
    const toSave = Array.from(merged.values());
    if (toDelete.length === 0 && toSave.length === 0) return undefined;
    try {
      // Edited existing demands are updated, new cells created (review round 5, finding 1.1)
      await persistStep1Demands(toDelete, toSave, DemandControllerService);
      if (toDelete.length > 0) setPendingDeletions([]);
      if (toSave.length > 0) {
        setPendingOrders([]);
        setPendingForecasts([]);
      }
      // Reload for the UI; the planning uses the merged data, because the state update is asynchronous
      const planningStart = formData.plannedStart?.split('T')[0];
      if (planningStart) {
        const [y, m, d] = planningStart.split('-').map(Number);
        const loadStartDate = new Date(y, m - 1, d);
        const loadEndDate = new Date(loadStartDate);
        const maxOffset = Math.max(0, ...materials.map((m: any) => Number(m?.longTermOffsetDays) || 0));
        loadEndDate.setDate(loadEndDate.getDate() + 6 + maxOffset);
        const loadEndStr = `${loadEndDate.getFullYear()}-${String(loadEndDate.getMonth() + 1).padStart(2, '0')}-${String(loadEndDate.getDate()).padStart(2, '0')}`;
        loadDemands(planningStart, loadEndStr);
      }
      // Return the merged display week demands right away (the state update is asynchronous)
      if (!planningStart) return undefined;
      const toDeleteSet = new Set(toDelete);
      // A demand that was edited is updated, not deleted, so it is not in toDeleteSet. Without
      // this second set its stale row stayed in the list and the toSave row was appended on top,
      // so a direct start right after an edit planned the old plus the new quantity - the very
      // double-counting finding 1.1 removes from the database. The merge() dedupe below does not
      // catch it: the stale row carries a real dueTime while the save carries none, so the keys
      // differ (review round 5, finding 1.1).
      const supersededUuids = savedUuidsOf(toSave);
      const isStale = (d: DemandDto) =>
        Boolean(d.uuid) && (toDeleteSet.has(d.uuid!) || supersededUuids.has(d.uuid!));
      const isForecast = (d: DemandDto) =>
        String(d.demandType || d.source || '').toUpperCase() === 'FORECAST';
      const isOrder = (d: DemandDto) =>
        String(d.demandType || d.source || 'ORDER').toUpperCase() === 'ORDER';
      const ords = [...demands.filter(isOrder).filter(d => !isStale(d)), ...toSave.filter(isOrder)];
      const fcasts = [...demands.filter(isForecast).filter(d => !isStale(d)), ...toSave.filter(isForecast)];
      const key = (d: DemandDto) => `${d.materialId || ''}|${d.demandDate?.split('T')[0] || ''}|${d.dueTime || ''}`;
      const merge = (o: DemandDto[], f: DemandDto[]) => {
        const orderKeys = new Set(o.map(key));
        return [...o, ...f.filter(x => !orderKeys.has(key(x)))];
      };
      const displayEndDate = new Date(planningStart + 'T12:00:00');
      displayEndDate.setDate(displayEndDate.getDate() + 6);
      const displayEndStr = `${displayEndDate.getFullYear()}-${String(displayEndDate.getMonth() + 1).padStart(2, '0')}-${String(displayEndDate.getDate()).padStart(2, '0')}`;
      const inDisplayWeek = (d: DemandDto) => {
        const dDate = d.demandDate?.split('T')[0];
        return dDate && dDate >= planningStart && dDate <= displayEndStr;
      };
      return merge(ords.filter(inDisplayWeek), fcasts.filter(inDisplayWeek));
    } catch (error) {
      handleError(error, 'saving demands');
      throw error;
    }
  }, [pendingOrders, pendingForecasts, pendingDeletions, loadDemands, handleError, formData.plannedStart, materials, demands]);

  // Load planning horizon settings
  const loadSettings = useCallback(async () => {
    try {
      setIsLoadingSettings(true);
      const raw = await SystemSettingControllerService.getPlanningHorizonSettings();
      setPlanningSettings({
        ...raw,
        defaultDays: 1,
        minDays: 1,
        maxDays: 1
      } as PlanningHorizonSettings);
      settingsLoadedRef.current = true;
    } catch (error) {
      handleError(error, 'loading planning horizon settings');
    } finally {
      setIsLoadingSettings(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleError may not be stable; loads only once
  }, []);

  // Effect 1: Load settings and materials when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (!settingsLoadedRef.current) {
        loadSettings();
      }
      // Always load materials when dialog opens, even if they exist (handles hot reload)
      // Use a small delay to ensure state is settled after hot reload
      const timeoutId = setTimeout(() => {
        loadMaterials();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, loadSettings, loadMaterials]);

  // Reset settings when dialog is closed
  useEffect(() => {
    if (!isOpen) {
      settingsLoadedRef.current = false;
      materialsLoadAttemptedRef.current = false;
      previewScheduleUuidRef.current = null;
      setIsLoadingSettings(true);
      setBatches([]);
      setDemands([]);
      setPendingOrders([]);
      setPendingForecasts([]);
      setPendingDeletions([]);
      setLocalBatchesMap({});
      setPendingIntermediateOrders([]);
      setMaterials([]); // Also reset materials when dialog closes
      setProductionOrders([]);
      setIsCreatingProductionOrders(false);
      setProductionOrderCreationProgress(0);
      setStep3ValidationError(null);
      setStep0ReleasedPlansLoading(true);
      setStep0HasReleasedPlans(false);
    }
  }, [isOpen]);

  // Auto-calculate batches when method changes or demands change
  useEffect(() => {
    if (demands.length > 0 && materials.length > 0) {
      const enrichedDemands = demands.map(demand => {
        const material = materials.find(m => m.uuid === demand.materialId);
        return {
          ...demand,
          material: material || { 
            uuid: demand.materialId, 
            name: demand.materialName, 
            scrapRate: 0.02, 
            initialWeighInGrams: 250,
            minDoughQuantityInKg: 0.0,
            maxDoughQuantityInKg: 250.0
          }
        };
      });

      // Group by effective material ID (alias → main product) so alias demands are merged with main product
      const materialGroups = enrichedDemands.reduce((acc, demand) => {
        const effectiveId = getEffectiveMaterialId(demand.materialId, materials) || demand.material?.uuid || 'unknown';
        const mainProduct = materials.find((m: any) => m.uuid === effectiveId);
        if (!acc[effectiveId]) {
          acc[effectiveId] = {
            material: mainProduct || demand.material,
            demands: []
          };
        }
        acc[effectiveId].demands.push(demand);
        return acc;
      }, {} as Record<string, { material: any; demands: any[] }>);

      const materialGroupsArray = Object.values(materialGroups);
      const selectedMaterial = materialGroupsArray[selectedMaterialIndex] || null;
      
      if (selectedMaterial) {
        const totalQuantity = selectedMaterial.demands?.reduce((sum: number, demand: any) => sum + (demand.quantity || 0), 0) || 0;
        const scrapRate = selectedMaterial.material?.scrapRate || 0.02;
        const scrapQuantity = Math.round(totalQuantity * scrapRate);
        const grossQuantity = totalQuantity + scrapQuantity;
        const maxBatchSize = selectedMaterial.material?.maxDoughQuantityInKg || 250;
        const minBatchSize = selectedMaterial.material?.minDoughQuantityInKg || 0;
        
        const initialWeightGrams = selectedMaterial.material?.initialWeighInGrams || 250;
        const materialMethod = selectedMaterial.material?.doughBatchMethod === 'FULL' ? 'full' : 'even';
        calculateBatches(grossQuantity, maxBatchSize, minBatchSize, materialMethod, initialWeightGrams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- calculateBatches intentionally excluded to avoid circular recalculation
  }, [demands, materials, selectedMaterialIndex]);

  // Update form data when settings are loaded (only if not already set)
  useEffect(() => {
    if (!isLoadingSettings && planningSettings.defaultDays) {
      // Helper function to create a full DateTime string (with time component) from a date
      const createDateTimeString = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T00:00:00`;
      };

      // Planning starts on the next day (initial value)
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      let endDate: Date;
      if (planningSettings.maxDays === 1) {
        endDate = new Date(tomorrow);
        endDate.setHours(23, 59, 59, 999);
      } else {
        endDate = new Date(tomorrow.getTime() + (planningSettings.defaultDays - 1) * 24 * 60 * 60 * 1000);
        endDate.setHours(23, 59, 59, 999);
      }
      const createEndDateTimeString = (date: Date): string => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const h = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const s = String(date.getSeconds()).padStart(2, '0');
        return `${y}-${m}-${d}T${h}:${min}:${s}`;
      };
      setFormData(prev => {
        const updates: Partial<ProductionScheduleDto> = {};
        if (!prev.plannedStart) {
          updates.plannedStart = createDateTimeString(tomorrow);
        }
        if (!prev.plannedEnd) {
          updates.plannedEnd = createEndDateTimeString(endDate);
        }
        return { ...prev, ...updates };
      });
    }
  }, [planningSettings, isLoadingSettings]);

  // Stable scalar derived from materials – only changes when actual offset values change
  const maxLongTermOffset = useMemo(
    () => Math.max(0, ...materials.map((m: any) => Number(m?.longTermOffsetDays) || 0)),
    [materials]
  );

  // Load demands when date range changes or when dialog opens
  // Display week plus the maximum longTermOffsetDays for long-term managed products (the offset is taken into account)
  useEffect(() => {
    if (isOpen && formData.plannedStart && formData.plannedEnd) {
      const startDateStr = formData.plannedStart.split('T')[0];
      const [y, m, d] = startDateStr.split('-').map(Number);
      const loadStartDate = new Date(y, m - 1, d);
      const loadEndDate = new Date(loadStartDate);
      loadEndDate.setDate(loadEndDate.getDate() + 6 + maxLongTermOffset);
      const loadEndDateString = `${loadEndDate.getFullYear()}-${String(loadEndDate.getMonth() + 1).padStart(2, '0')}-${String(loadEndDate.getDate()).padStart(2, '0')}`;
      const dateRangeKey = `${startDateStr}-${loadEndDateString}`;
      if (!lastLoadedDateRangeRef.current || lastLoadedDateRangeRef.current !== dateRangeKey) {
        lastLoadedDateRangeRef.current = dateRangeKey;
        loadDemands(startDateStr, loadEndDateString);
      }
    } else {
      demandsLoadedRef.current = false;
      lastLoadedDateRangeRef.current = null;
    }
  }, [isOpen, formData.plannedStart, formData.plannedEnd, loadDemands, maxLongTermOffset]);

  const generateDefaultScheduleName = useCallback(() => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('de-DE', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
    const timeStr = now.toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    return `Produktionsplan ${dateStr} ${timeStr}`;
  }, []);

  // Split demands by type, merge with pending, and compute effective (merged) demands for planning
  // Rule: For each (materialId, demandDate), order wins over forecast
  // Exclude demands marked for deletion (user set quantity to 0)
  const { orders, forecasts, effectiveDemands, displayEffectiveDemands, allMergedDemands } = useMemo(() => {
    const planningStart = formData.plannedStart?.split('T')[0];
    const planningEnd = formData.plannedEnd?.split('T')[0];
    if (!planningStart || !planningEnd) {
      return { orders: [], forecasts: [], effectiveDemands: [], displayEffectiveDemands: [], allMergedDemands: [] };
    }
    const toDelete = new Set(pendingDeletions);
    const isForecast = (d: DemandDto) =>
      String(d.demandType || d.source || '').toUpperCase() === 'FORECAST';
    const isOrder = (d: DemandDto) =>
      String(d.demandType || d.source || 'ORDER').toUpperCase() === 'ORDER';
    const materialDateKey = (d: DemandDto) => `${d.materialId || ''}|${d.demandDate?.split('T')[0] || ''}`;
    const pendingOrderKeys = new Set(pendingOrders.map(materialDateKey));
    const pendingForecastKeys = new Set(pendingForecasts.map(materialDateKey));
    const ords = [
      ...demands.filter(isOrder).filter(d => !pendingOrderKeys.has(materialDateKey(d))),
      ...pendingOrders
    ].filter(d => !d.uuid || !toDelete.has(d.uuid));
    const fcasts = [
      ...demands.filter(isForecast).filter(d => !pendingForecastKeys.has(materialDateKey(d))),
      ...pendingForecasts
    ].filter(d => !d.uuid || !toDelete.has(d.uuid));
    const key = (d: DemandDto) => `${d.materialId || ''}|${d.demandDate?.split('T')[0] || ''}|${d.dueTime || ''}`;
    const merge = (ordersList: DemandDto[], forecastsList: DemandDto[]) => {
      const orderKeys = new Set(ordersList.map(key));
      return [...ordersList, ...forecastsList.filter(f => !orderKeys.has(key(f)))];
    };
    const allMerged = merge(ords, fcasts);
    // Planning horizon (for steps 2 and 3)
    const inPlanningHorizon = (d: DemandDto) => {
      const dDate = d.demandDate?.split('T')[0];
      return dDate && dDate >= planningStart && dDate <= planningEnd;
    };
    const ordersInRange = ords.filter(inPlanningHorizon);
    const forecastsInRange = fcasts.filter(inPlanningHorizon);
    const effective = merge(ordersInRange, forecastsInRange);
    // Production quantities and offset: at least 7 days from the planning start, with a longer horizon up to plannedEnd (source data)
    const displayEndDate = new Date(planningStart + 'T12:00:00');
    displayEndDate.setDate(displayEndDate.getDate() + 6);
    const displayEndStr = `${displayEndDate.getFullYear()}-${String(displayEndDate.getMonth() + 1).padStart(2, '0')}-${String(displayEndDate.getDate()).padStart(2, '0')}`;
    const sourceDemandWindowEnd = planningEnd > displayEndStr ? planningEnd : displayEndStr;
    const inDisplayWeek = (d: DemandDto) => {
      const dDate = d.demandDate?.split('T')[0];
      return dDate && dDate >= planningStart && dDate <= sourceDemandWindowEnd;
    };
    const ordersInDisplayWeek = ords.filter(inDisplayWeek);
    const forecastsInDisplayWeek = fcasts.filter(inDisplayWeek);
    const displayEffective = merge(ordersInDisplayWeek, forecastsInDisplayWeek);
    return { orders: ords, forecasts: fcasts, effectiveDemands: effective, displayEffectiveDemands: displayEffective, allMergedDemands: allMerged };
  }, [demands, pendingOrders, pendingForecasts, pendingDeletions, formData.plannedStart, formData.plannedEnd]);

  // Long-term offset on the production date: all demands (allMerged). The batch buckets are derived from productionDisplayDemandsAggregated.
  const productionEffectiveDemands = useMemo(() => {
    return allMergedDemands.map(d => {
      const mat = materialForLongTermPlanningRow(d, materials);
      const isLongRunning = mat?.longRunning === true;
      const offsetDays = Math.max(0, Number(mat?.longTermOffsetDays) || 0);
      if (!isLongRunning || offsetDays <= 0) return d;
      const dStr = d.demandDate?.split('T')[0];
      if (!dStr) return d;
      const [y, m, day] = dStr.split('-').map(Number);
      const demandDate = new Date(y, m - 1, day);
      demandDate.setDate(demandDate.getDate() - offsetDays);
      const prodStr = `${demandDate.getFullYear()}-${String(demandDate.getMonth() + 1).padStart(2, '0')}-${String(demandDate.getDate()).padStart(2, '0')}`;
      return { ...d, demandDate: `${prodStr}T00:00:00` };
    });
  }, [allMergedDemands, materials]);

  // For the production quantities tab: displayEffectiveDemands with the long-term offset (window: see inDisplayWeek)
  const productionDisplayDemands = useMemo(() => {
    return displayEffectiveDemands.map(d => {
      const mat = materialForLongTermPlanningRow(d, materials);
      const isLongRunning = mat?.longRunning === true;
      const offsetDays = Math.max(0, Number(mat?.longTermOffsetDays) || 0);
      if (!isLongRunning || offsetDays <= 0) return d;
      const dStr = d.demandDate?.split('T')[0];
      if (!dStr) return d;
      const [y, m, day] = dStr.split('-').map(Number);
      const demandDate = new Date(y, m - 1, day);
      demandDate.setDate(demandDate.getDate() - offsetDays);
      const prodStr = `${demandDate.getFullYear()}-${String(demandDate.getMonth() + 1).padStart(2, '0')}-${String(demandDate.getDate()).padStart(2, '0')}`;
      return { ...d, demandDate: `${prodStr}T00:00:00` };
    });
  }, [displayEffectiveDemands, materials]);

  // For the production quantities table: aggregate by (effectiveId, demandDate) - the quantity per date is the sum of alias and main on the same date only.
  // Dense for the 7 day display from plannedStart: exactly one row per main product and day; a missing order or forecast becomes quantity 0 (source EFFECTIVE), the basis for the batch and UI totals.
  // Days outside that week (with a long horizon) come only from the real demand lines, as before.
  const productionDisplayDemandsAggregated = useMemo((): DemandDto[] => {
    const planningStart = formData.plannedStart?.split('T')[0];
    const byProductAndDate = new Map<string, Map<string, { quantity: number; dueTime?: string }>>();
    for (const d of productionDisplayDemands) {
      const effectiveId = getEffectiveMaterialId(d.materialId, materials) || d.materialId;
      if (!effectiveId) continue;
      const main = materials.find((m: any) => m.uuid === effectiveId);
      if (!main) continue;
      const dateKey = d.demandDate?.split('T')[0] || '';
      if (!dateKey) continue;
      const qty = d.quantity ?? 0;
      if (!byProductAndDate.has(effectiveId)) byProductAndDate.set(effectiveId, new Map());
      const dateMap = byProductAndDate.get(effectiveId)!;
      const existing = dateMap.get(dateKey);
      if (existing) {
        existing.quantity += qty;
        if (d.dueTime && !existing.dueTime) existing.dueTime = d.dueTime;
      } else {
        dateMap.set(dateKey, { quantity: qty, dueTime: d.dueTime });
      }
    }

    const weekDateSet = new Set<string>();
    const weekDatesOrdered: string[] = [];
    if (planningStart) {
      const [y, mo, da] = planningStart.split('-').map(Number);
      const base = new Date(y, mo - 1, da);
      for (let i = 0; i < 7; i++) {
        const day = new Date(base);
        day.setDate(base.getDate() + i);
        const ds = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
        weekDateSet.add(ds);
        weekDatesOrdered.push(ds);
      }
    }

    const mainProducts = (materials as any[])
      .filter((m: any) => m.materialType === 'PRODUCT' && !m.isAlias)
      .slice()
      .sort((a: any, b: any) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );

    const result: DemandDto[] = [];

    if (!planningStart || weekDatesOrdered.length === 0) {
      byProductAndDate.forEach((dateMap, effectiveId) => {
        const main = materials.find((m: any) => m.uuid === effectiveId) as any;
        const materialName = main?.name || effectiveId;
        Array.from(dateMap.keys())
          .sort()
          .forEach(dateKey => {
            const v = dateMap.get(dateKey)!;
            result.push({
              uuid: `${effectiveId}|${dateKey}`,
              materialId: effectiveId,
              materialName,
              demandDate: `${dateKey}T00:00:00`,
              dueTime: v.dueTime,
              quantity: v.quantity
            } as DemandDto);
          });
      });
      return result;
    }

    for (const main of mainProducts) {
      const effectiveId = main.uuid as string;
      if (!effectiveId) continue;
      const materialName = main.name || effectiveId;
      const dateMap = byProductAndDate.get(effectiveId) ?? new Map<string, { quantity: number; dueTime?: string }>();

      for (const dateKey of weekDatesOrdered) {
        const existing = dateMap.get(dateKey);
        if (existing) {
          result.push({
            uuid: `${effectiveId}|${dateKey}`,
            materialId: effectiveId,
            materialName,
            demandDate: `${dateKey}T00:00:00`,
            dueTime: existing.dueTime,
            quantity: existing.quantity
          } as DemandDto);
        } else {
          result.push({
            uuid: `${effectiveId}|${dateKey}|net0`,
            materialId: effectiveId,
            materialName,
            demandDate: `${dateKey}T00:00:00`,
            quantity: 0,
            source: 'EFFECTIVE'
          } as DemandDto);
        }
      }

      const tailDates = Array.from(dateMap.keys())
        .filter(d => !weekDateSet.has(d))
        .sort();
      for (const dateKey of tailDates) {
        const v = dateMap.get(dateKey)!;
        result.push({
          uuid: `${effectiveId}|${dateKey}`,
          materialId: effectiveId,
          materialName,
          demandDate: `${dateKey}T00:00:00`,
          dueTime: v.dueTime,
          quantity: v.quantity
        } as DemandDto);
      }
    }

    return result;
  }, [productionDisplayDemands, materials, formData.plannedStart]);

  // Batch determination: the production quantities aggregate, strictly on the planning calendar [plannedStart, plannedEnd] (date part only).
  // Not the 7 column display from step 1 - that would aggregate days outside the horizon as well.
  const demandBucketsByProduct = useMemo((): Record<string, DemandDateBucket[]> => {
    const planningStart = formData.plannedStart?.split('T')[0];
    const planningEnd = formData.plannedEnd?.split('T')[0];
    if (!planningStart || !planningEnd) return {};
    const result: Record<string, DemandDateBucket[]> = {};
    for (const d of productionDisplayDemandsAggregated) {
      const materialId = d.materialId;
      if (!materialId) continue;
      const dateKey = d.demandDate?.split('T')[0] || '';
      if (!dateKey || dateKey < planningStart || dateKey > planningEnd) continue;
      const qty = d.quantity ?? 0;
      if (qty <= 0) continue;
      const demandDateFull = d.demandDate?.includes('T') ? d.demandDate : `${dateKey}T00:00:00`;
      if (!result[materialId]) result[materialId] = [];
      result[materialId].push({
        demandDate: demandDateFull,
        quantity: qty,
        dueTime: d.dueTime
      });
    }
    Object.keys(result).forEach(k => {
      result[k].sort((a, b) => a.demandDate.localeCompare(b.demandDate));
    });
    return result;
  }, [productionDisplayDemandsAggregated, formData.plannedStart, formData.plannedEnd]);

  // Dev: batch buckets versus the production quantities aggregate (console: filter for "WizardBatchQty")
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const planningStart = formData.plannedStart?.split('T')[0];
    const planningEnd = formData.plannedEnd?.split('T')[0];
    if (!planningStart || !planningEnd) return;
    const productIdsInSequence = new Set(
      sequenceEntries
        .map(e => e.productId)
        .filter((id): id is string => typeof id === 'string' && id.length > 0)
    );

    const productsInSequence = Array.from(productIdsInSequence).map(pid => {
      const mat = materials.find((m: { uuid?: string }) => m.uuid === pid) as any;
      const rowsAgg = productionDisplayDemandsAggregated.filter(d => d.materialId === pid);
      const rowsWithHorizonFlag = rowsAgg.map(d => {
        const dateKey = d.demandDate?.split('T')[0] || '';
        const includedInDemandBuckets = !!(
          dateKey &&
          planningStart &&
          planningEnd &&
          dateKey >= planningStart &&
          dateKey <= planningEnd
        );
        return {
          productionDate: dateKey,
          quantity: d.quantity ?? 0,
          includedInDemandBuckets,
          excludedFromBucketsBecause: includedInDemandBuckets
            ? null
            : `productionDate ${dateKey} not in planning horizon [${planningStart}, ${planningEnd}]`
        };
      });
      const buckets = demandBucketsByProduct[pid] ?? [];
      const bucketSum = buckets.reduce((s, b) => s + (b.quantity || 0), 0);
      const effRows = effectiveDemands
        .filter(
          d => getEffectiveMaterialId(d.materialId, materials) === pid || d.materialId === pid
        )
        .map(d => ({
          sourceDate: d.demandDate?.split('T')[0],
          quantity: d.quantity,
          demandType: d.demandType ?? (d as { source?: string }).source
        }));

      return {
        materialId: pid,
        materialName: mat?.name,
        longRunning: mat?.longRunning === true,
        longTermOffsetDays: mat?.longTermOffsetDays,
        materialType: mat?.materialType,
        productionDisplayAggregatedRows: rowsWithHorizonFlag,
        sumAggregatedAllDates: rowsAgg.reduce((s, d) => s + (d.quantity ?? 0), 0),
        sumAggregatedInPlanningHorizon: rowsWithHorizonFlag
          .filter(r => r.includedInDemandBuckets)
          .reduce((s, r) => s + r.quantity, 0),
        demandBuckets: buckets,
        bucketSum,
        effectiveDemandsInPlanningHorizon: effRows
      };
    });

    logWizardBatchQty('context.snapshot', {
      plannedStartRaw: formData.plannedStart,
      plannedEndRaw: formData.plannedEnd,
      planningStartDate: planningStart,
      planningEndDate: planningEnd,
      aggregatedRowCount: productionDisplayDemandsAggregated.length,
      materialCountWithBuckets: Object.keys(demandBucketsByProduct).length,
      productsInSequence
    });
  }, [
    productionDisplayDemandsAggregated,
    demandBucketsByProduct,
    formData.plannedStart,
    formData.plannedEnd,
    sequenceEntries,
    materials,
    effectiveDemands
  ]);

  const convertDemandsForView = useCallback((demandsToConvert: DemandDto[]) => {
    return demandsToConvert.map(demand => ({
      id: demand.uuid || '',
      materialName: demand.materialName || '',
      demandDate: demand.demandDate || '',
      dueTime: demand.dueTime || '',
      quantity: demand.quantity ?? 0,
      source: (demand.demandType || demand.source || 'ORDER') as string,
      priority: demand.priority ?? 1
    }));
  }, []);

  // Helper function to create a full DateTime string (start of day 00:00:00) from a date
  const createDateTimeString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
  };

  // Helper function to create end-of-day DateTime string (23:59:59) from a date
  const createEndDateTimeString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T23:59:59`;
  };

  // Helper to ensure date value is in full DateTime format. For plannedEnd, preserve end-of-day (23:59:59)
  // to avoid infinite loop: ensureDateTimeFormat converted 23:59:59→00:00:00, useEffect then set 23:59:59 again.
  const ensureDateTimeFormatForField = (field: 'plannedStart' | 'plannedEnd', dateValue: string | Date | undefined): string | undefined => {
    if (!dateValue) return undefined;
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (isNaN(date.getTime())) return undefined;
    return field === 'plannedEnd' ? createEndDateTimeString(date) : createDateTimeString(date);
  };

  const updateFormData = useCallback((field: keyof ProductionScheduleDto, value: any) => {
    if (field === 'plannedStart' || field === 'plannedEnd') {
      value = ensureDateTimeFormatForField(field, value);
    }

    setFormData(prev => {
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ensureDateTimeFormatForField is a local function; including it would run on every render
  }, []);

  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }, [i18n.language]);

  // Validate step 3: check whether all batches have a routing assigned, or whether there are finish-baking orders
  const validateStep3 = useCallback((): { isValid: boolean; batchesWithoutRouting: BatchData[] } => {
    // At least one material with batches (quantities > 0), or orders to finish baking for this plan
    const hasBatches = Object.values(batchDeterminationData).some(batchData =>
      batchData.campaigns && batchData.campaigns.length > 0 &&
      batchData.campaigns.some(campaign => campaign.doughBatches.length > 0)
    );

    if (hasBatches || hasFollowUpOrders) {
      return { isValid: true, batchesWithoutRouting: [] };
    }

    return {
      isValid: false,
      batchesWithoutRouting: []
    };
  }, [batchDeterminationData, hasFollowUpOrders]);

  // Create production orders for all batches with routing (only in memory, not in DB)
  const createProductionOrders = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    // Collect all batches with routing assignments
    const batchesWithRouting: BatchData[] = [];
    Object.values(localBatchesMap).forEach(materialBatches => {
      materialBatches.forEach(batch => {
        // Only check if routingId is present
        if (batch.routingId) {
          batchesWithRouting.push(batch);
        }
      });
    });

    if (batchesWithRouting.length === 0) {
      return { success: false, error: 'No batches with routing assignments found' };
    }

    setIsCreatingProductionOrders(true);
    setProductionOrderCreationProgress(0);

    try {
      // Create production orders only in memory (not in DB yet)
      // They will be saved to DB when the wizard is completed
      const createdOrders: ProductionOrderDto[] = batchesWithRouting.map((batch, index) => {
        // Simulate progress
        setProductionOrderCreationProgress(Math.round(((index + 1) / batchesWithRouting.length) * 100));
        
        return {
          uuid: undefined, // Will be generated by backend when saved
          orderNumber: `PO-${String(index + 1).padStart(6, '0')}`, // Temporary number
          description: `Production order for batch: ${batch.id} (${batch.materialName})`,
          routingId: batch.routingId,
          materialId: batch.materialId || '',
          outputQuantity: batch.quantity,
          status: 'PLANNED'
        } as ProductionOrderDto;
      });

      setProductionOrders(createdOrders);
      setProductionOrderCreationProgress(100);
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      handleError(error, 'creating production orders');
      return { success: false, error: errorMessage };
    } finally {
      setIsCreatingProductionOrders(false);
    }
  }, [localBatchesMap, handleError]);

  // Delete all production orders created in the wizard (only from memory, not from DB)
  const deleteProductionOrders = useCallback(async (): Promise<void> => {
    // Since production orders are only in memory until the wizard is saved,
    // we just need to clear them from the context
    setProductionOrders([]);
  }, []);

  const clearStep3ValidationError = useCallback(() => {
    setStep3ValidationError(null);
  }, []);

  const validateSequenceEntries = useCallback((): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (sequenceEntries.length === 0) {
      errors.push(t('campaignSequence.wizard.validation.noEntries', 'Mindestens ein Eintrag ist erforderlich'));
      return { isValid: false, errors };
    }

    // Group entries by product UUID to check for multiple occurrences
    const entriesByProduct = new Map<string, CampaignSequenceEntryDto[]>();
    sequenceEntries.forEach(entry => {
      if (entry.productId) {
        const existing = entriesByProduct.get(entry.productId) || [];
        existing.push(entry);
        entriesByProduct.set(entry.productId, existing);
      }
    });

    // Validate maxBatches rules
    entriesByProduct.forEach((productEntries, productId) => {
      const productName = productEntries[0]?.productName || productId;
      
      // Check if any entry has maxBatches set
      const hasMaxBatches = productEntries.some(entry => 
        entry.maxBatches !== null && entry.maxBatches !== undefined && entry.maxBatches > 0
      );
      
      if (hasMaxBatches) {
        // If maxBatches is set, product must occur at least twice
        if (productEntries.length < 2) {
          errors.push(t('campaignSequence.wizard.validation.maxBatchesRequiresMultiple', 
            'Für Produkt "{{productName}}" ist "Max Teig-Chargen" gesetzt, aber es gibt nur einen Eintrag. Bei gesetztem "Max Teig-Chargen" muss das Produkt mindestens zweimal in der Sequenz vorkommen.',
            { productName }));
          return;
        }
        
        // Sort by sequenceNumber
        const sorted = [...productEntries].sort((a, b) => 
          (a.sequenceNumber || 0) - (b.sequenceNumber || 0)
        );
        
        // Last entry must have maxBatches = null
        const lastEntry = sorted[sorted.length - 1];
        if (lastEntry.maxBatches !== null && lastEntry.maxBatches !== undefined) {
          errors.push(t('campaignSequence.wizard.validation.lastEntryMustBeUnlimited',
            'Der letzte Eintrag für Produkt "{{productName}}" muss unbegrenzt sein (Max Teig-Chargen = leer)',
            { productName }));
        }
        
        // Previous entries must have maxBatches > 0
        for (let i = 0; i < sorted.length - 1; i++) {
          const entry = sorted[i];
          if (entry.maxBatches === null || entry.maxBatches === undefined || entry.maxBatches <= 0) {
            errors.push(t('campaignSequence.wizard.validation.previousEntryMustHaveMaxBatches',
              'Eintrag für Produkt "{{productName}}" (Position {{sequenceNumber}}) muss einen positiven Wert für "Max Teig-Chargen" haben',
              { productName, sequenceNumber: entry.sequenceNumber || i + 1 }));
          }
        }
      } else {
        // Single occurrence: maxBatches can be null or > 0
        if (productEntries.length === 1) {
          const entry = productEntries[0];
          if (entry.maxBatches !== null && entry.maxBatches !== undefined && entry.maxBatches <= 0) {
            errors.push(t('campaignSequence.wizard.validation.invalidMaxBatches',
              'Max Teig-Chargen für Produkt "{{productName}}" muss größer als 0 oder leer sein',
              { productName }));
          }
        }
      }
    });

    return { isValid: errors.length === 0, errors };
  }, [sequenceEntries, t]);

  // Validate routings for step 3 - only checks active routings
  const validateRoutingsForStep3 = useCallback(async (): Promise<RoutingValidationIssue[]> => {
    // 1. Collect all material ids from batchDeterminationData (with campaigns)
    const materialIds = Object.values(batchDeterminationData)
      .filter(batchData => batchData.campaigns && batchData.campaigns.some(campaign => campaign.doughBatches.length > 0))
      .map(batchData => batchData.materialId)
      .filter((materialId): materialId is string => !!materialId);
    
    const uniqueMaterialIds = Array.from(new Set(materialIds));
    if (uniqueMaterialIds.length === 0) {
      return [];
    }
    
    // 2. Build the material map
    const materialsById = new Map<string, { name?: string }>();
    materials.forEach((material: any) => {
      if (material?.uuid) {
        materialsById.set(material.uuid, material);
      }
    });
    
    // 3. Load the resource types for the processing line check
    const resourceTypes = await ResourceTypeControllerService.findAll();
    const resourceTypeById = new Map(resourceTypes.map(rt => [rt.uuid || '', rt]));
    
    // 4. For each material: find the active routing
    const issues: RoutingValidationIssue[] = [];
    for (const materialId of uniqueMaterialIds) {
      const materialName = materialsById.get(materialId)?.name || batchDeterminationData[materialId]?.materialName || materialId;
      
      // Find the active routing (only active routings are returned)
      let activeRouting = null;
      try {
        activeRouting = await RoutingControllerService.getActiveRoutingForMaterial(materialId);
      } catch (error: any) {
        // 404 means: no active routing found - that is fine, we treat it as a missing routing
        if (error?.response?.status === 404 || error?.status === 404) {
          activeRouting = null;
        } else {
          // Re-throw other errors
          throw error;
        }
      }
      
      if (!activeRouting) {
        issues.push({
          materialId,
          materialName,
          reason: 'missingRouting'
        });
        continue;
      }
      
      // Check whether the routing status is COMPLETE (it has to be active AND complete)
      if (activeRouting.status !== 'COMPLETE') {
        issues.push({
          materialId,
          materialName,
          routingName: activeRouting.name,
          reason: 'incompleteRouting'
        });
        continue;
      }
      
      // Check whether the routing has a processing line
      const hasProcessingLine = (activeRouting.operations || []).some(op => {
        const resourceTypeId = op.primaryResourceTypeId;
        if (!resourceTypeId) {
          return false;
        }
        const resourceType = resourceTypeById.get(resourceTypeId);
        return resourceType?.category === 'PROCESSING_LINE';
      });
      
      if (!hasProcessingLine) {
        issues.push({
          materialId,
          materialName,
          routingName: activeRouting.name,
          reason: 'missingProcessingLine'
        });
      }
    }
    
    return issues;
  }, [batchDeterminationData, materials]);

  const handleNext = useCallback(async () => {
    // Step 0 -> step 1: only proceed if a planning mode is chosen; for replanning only the source plan (end of the frozen zone in step 1)
    if (currentStep === 0) {
      if (planningMode === 'REPLANNING' && !sourceScheduleUuid) {
        showToast('info', t('common.validation', 'Validierung'), t('productionSchedule.wizard.step0.umplanungRequired', 'Bitte wählen Sie den Quellplan.'));
        return;
      }
      setCurrentStep(1);
      return;
    }

    // Step 1 -> step 2: for replanning the end of the frozen zone has to be set; no row with new target < actual; locked campaigns unchanged
    if (currentStep === 1) {
      if (planningMode === 'REPLANNING' && step1ReplanningHasNewSollBelowIst) {
        showToast('warning', t('common.validation', 'Validierung'), t('productionSchedule.wizard.step1.replanning.newSollBelowIst', 'Neue Soll-Menge darf nicht unter der Ist-Menge liegen.'));
        return;
      }
      if (planningMode === 'REPLANNING' && !frozenZoneEnd) {
        showToast('warning', t('common.validation', 'Validierung'), t('productionSchedule.wizard.step1.umplanungFrozenZoneRequired', 'Bitte zuerst das Frozen-Zone-Ende auswählen und bestätigen.'));
        return;
      }
      const flushResult = step1FlushRef.current?.() ?? { saves: [], clears: [] };
      let savedDemands: DemandDto[] | undefined;
      try {
        savedDemands = await savePendingStep1Demands(flushResult);
      } catch {
        return; // Stay on step 1 if save fails (handleError already showed message)
      }
      // REPLANNING: products with demand that are not in the source plan's sequence snapshot are
      // appended to the end of the sequence right away - synchronously, without an API call.
      if (planningMode === 'REPLANNING') {
        const demandsToCheck = savedDemands ?? displayEffectiveDemands;
        const productIdsInSequence = new Set(sequenceEntries.map((e) => e.productId).filter(Boolean));
        const newProductIds = new Set<string>();
        demandsToCheck.forEach((d: DemandDto) => {
          if (!d.materialId || (d.quantity ?? 0) <= 0) return;
          const material = materials.find((m: { uuid?: string }) => m.uuid === d.materialId);
          if (!material || material.materialType !== 'PRODUCT') return;
          const effectiveId = getEffectiveMaterialId(d.materialId, materials) ?? d.materialId;
          if (!productIdsInSequence.has(effectiveId)) newProductIds.add(effectiveId);
        });
        if (newProductIds.size > 0) {
          const maxSeq = sequenceEntries.length > 0
            ? Math.max(...sequenceEntries.map((e) => e.sequenceNumber ?? 0))
            : 0;
          const appended: CampaignSequenceEntryDto[] = Array.from(newProductIds).map((productId, i) => {
            const material = materials.find((m: { uuid?: string }) => m.uuid === productId);
            return {
              uuid: generateUUID(),
              productId,
              productName: material?.name ?? '',
              sequenceNumber: maxSeq + i + 1,
            };
          });
          setSequenceEntries((prev) => [...prev, ...appended]);
        }
      }
      setCurrentStep(2);
      return;
    }

    // Special handling for Step 2 → Step 3 transition (Sequence Selection)
    if (currentStep === 2) {
      // Skip sequence validation when using automatic sequence optimization
      if (!sequenceOptimization) {
        const validation = validateSequenceEntries();

        if (!validation.isValid) {
          const errorMessage = validation.errors.join('\n');
          showToast('info', t('campaignSequence.wizard.validation.title', 'Validierungsfehler'), errorMessage);
          return;
        }
      }

      // Move to Step 3 (Batch Creation)
      setCurrentStep(3);
      return;
    }

    // Special handling for Step 3 → Step 4 transition (Batch Creation → Review)
    if (currentStep === 3) {
      // 1. First the existing step 3 validation (batches present)
      const validation = validateStep3();
      
      if (!validation.isValid) {
        showToast('info', t('productionSchedule.wizard.step3.validation.noBatches', 'Keine Chargen'),
          t('productionSchedule.wizard.step3.validation.noBatchesOrAusbackenMessage', 'Bitte erstellen Sie mindestens eine Charge oder es müssen Ausbacken-Aufträge für diesen Plan vorhanden sein.'));
        return;
      }
      
      // 2. Run the routing validation (only active routings)
      const routingValidationIssues = await validateRoutingsForStep3();
      
      // 3. On errors: show the dialog, do not change step
      if (routingValidationIssues.length > 0) {
        setRoutingValidationIssues(routingValidationIssues); // state for the dialog
        return; // do not change step
      }
      
      // 4. No errors: move on to step 4 (summary)
      setStep3ValidationError(null);
      setRoutingValidationIssues(null); // Clear validation issues

      // Production orders are only created on save, not here.
      // The backend derives the intermediate product orders from the DbDemand entries
      // (see IntermediateProductOrderService.createOrdersFromDemands).
      setCurrentStep(4);
      return;
    }

    // Step 4 (summary) is the last step - there is no Next any more.
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sequenceOptimization and validateRoutingsForStep3 intentionally excluded to avoid cascading re-renders from state captured in validateRoutingsForStep3
  }, [currentStep, planningMode, sourceScheduleUuid, frozenZoneEnd, step1ReplanningHasNewSollBelowIst, validateSequenceEntries, validateStep3, showToast, t, savePendingStep1Demands, displayEffectiveDemands, sequenceEntries, materials, setSequenceEntries]);

  const handlePrevious = useCallback(async () => {
    // Step 4 (summary) -> step 3 (batches): remove the production orders that were created, so
    // that they are created fresh on the next "Next".
    if (currentStep === 4) {
      await deleteProductionOrders();
      setCurrentStep(3);
      return;
    }

    // Normal step regression
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, deleteProductionOrders]);

  const registerDirectStartSave = useCallback((fn: (() => Promise<void>) | null) => {
    directStartSaveRef.current = fn;
  }, []);

  const step1FlushRef = useRef<(() => Step1FlushPendingEditsResult) | null>(null);
  const registerStep1FlushPendingEdits = useCallback((fn: (() => Step1FlushPendingEditsResult) | null) => {
    step1FlushRef.current = fn;
  }, []);

  const step2aIntermediateFlushRef = useRef<(() => Promise<boolean>) | null>(null);
  const registerStep2aIntermediateFlush = useCallback((fn: (() => Promise<boolean>) | null) => {
    step2aIntermediateFlushRef.current = fn;
  }, []);
  const step2aIntermediateCleanupRef = useRef<(() => Promise<void>) | null>(null);
  const registerStep2aIntermediateCleanup = useCallback((fn: (() => Promise<void>) | null) => {
    step2aIntermediateCleanupRef.current = fn;
  }, []);

  const updateBatch = useCallback((batchId: string, updates: Partial<BatchData>) => {
    setBatches(prev => prev.map(batch => 
      batch.id === batchId ? { ...batch, ...updates } : batch
    ));
  }, []);

  const updateBatchesForMaterial = useCallback((_materialId: string, newBatches: BatchData[]) => {
    setBatches(newBatches);
  }, []);

  // Campaign Sequence actions (Step 2)
  const addSequenceEntry = useCallback((entry: CampaignSequenceEntryDto) => {
    setSequenceEntries(prev => {
      const maxSequenceNumber = prev.length > 0 
        ? Math.max(...prev.map(e => e.sequenceNumber || 0))
        : 0;
      const newEntry = {
        ...entry,
        sequenceNumber: maxSequenceNumber + 1
      };
      return [...prev, newEntry];
    });
  }, []);

  const updateSequenceEntry = useCallback((uuid: string, updates: Partial<CampaignSequenceEntryDto>) => {
    setSequenceEntries(prev => prev.map(entry => 
      entry.uuid === uuid ? { ...entry, ...updates } : entry
    ));
  }, []);

  const deleteSequenceEntry = useCallback((uuid: string) => {
    // Don't allow deletion of auto-added entries
    if (autoAddedProductIds.has(uuid)) {
      return;
    }
    
    setSequenceEntries(prev => {
      const filtered = prev.filter(entry => entry.uuid !== uuid);
      // Re-sequence numbers
      return filtered.map((entry, index) => ({
        ...entry,
        sequenceNumber: index + 1
      }));
    });
  }, [autoAddedProductIds]);

  // New batch actions (phase 1)
  const setBatchDeterminationData = useCallback((materialId: string, data: WizardBatchData) => {
    setBatchDeterminationDataState(prev => ({
      ...prev,
      [materialId]: data
    }));
  }, []);

  const setBatchDeterminationOverride = useCallback((materialId: string, override: BatchDeterminationOverride) => {
    setBatchDeterminationOverrides(prev => ({
      ...prev,
      [materialId]: override
    }));
  }, []);

  const removeBatchDeterminationData = useCallback((materialIds: string[]) => {
    if (!materialIds.length) {
      return;
    }

    setBatchDeterminationDataState(prev => {
      const next = { ...prev };
      materialIds.forEach(materialId => {
        delete next[materialId];
      });
      return next;
    });

    setBatchDeterminationOverrides(prev => {
      const next = { ...prev };
      materialIds.forEach(materialId => {
        delete next[materialId];
      });
      return next;
    });
    
    // Clear routing validation issues when campaigns are removed
    setRoutingValidationIssues(null);
  }, []);

  /** Set production quantities to 0 and lock fields for materials without work plan (instead of removing campaigns). */
  const setCampaignsWithoutRoutingToZero = useCallback((materialIds: string[]) => {
    if (!materialIds.length) {
      return;
    }

    setBatchDeterminationDataState(prev => {
      const next = { ...prev };
      materialIds.forEach(materialId => {
        const current = next[materialId];
        if (!current) return;
        next[materialId] = {
          ...current,
          desiredQuantity: 0,
          bakedQuantity: 0,
          doughQuantity: 0,
          doughBatches: [],
          ovenBatches: [],
          campaigns: current.campaigns.map(c => ({
            ...c,
            desiredQuantity: 0,
            totalBakedQuantity: 0,
            doughBatches: [],
            ovenBatches: []
          }))
        };
      });
      return next;
    });

    setMaterialIdsQuantityLocked(prev => {
      const next = { ...prev };
      materialIds.forEach(materialId => { next[materialId] = true; });
      return next;
    });
    setRoutingValidationIssues(null);
  }, []);

  // Calculation logic for the batch determination
  // desiredQuantityOrDateBuckets: number = one total quantity (legacy); DemandDateBucket[] = time-related buckets (quantity per date, dueDate per bucket).
  const calculateBatchDetermination = useCallback((
    materialId: string,
    desiredQuantityOrDateBuckets: number | DemandDateBucket[],
    doughBatchMethod?: 'even' | 'full',
    fixedDoughBatchesOverride?: DoughBatch[]
  ) => {
    const fixedDoughBatches = fixedDoughBatchesOverride ?? (planningMode === 'REPLANNING' ? fixedDoughBatchesByMaterial[materialId] : undefined);
    const material = materials.find(m => m.uuid === materialId);
    if (!material) {
      console.warn('Material not found for batch determination:', materialId);
      return;
    }
    logWizardBatchQty('calc.start', {
      materialId,
      materialName: material.name,
      planningMode,
      isReplan: planningMode === 'REPLANNING',
      input: typeof desiredQuantityOrDateBuckets === 'number'
        ? { kind: 'number', value: desiredQuantityOrDateBuckets }
        : { kind: 'buckets', count: desiredQuantityOrDateBuckets.length, total: desiredQuantityOrDateBuckets.reduce((s, b) => s + (b.quantity || 0), 0), buckets: desiredQuantityOrDateBuckets },
      fixedDoughBatchesCount: fixedDoughBatches?.length ?? 0,
      fixedDoughBatches: (fixedDoughBatches ?? []).map(b => ({ seq: b.sequenceNumber, qty: b.quantity, kg: b.doughQuantityKg, uuid: b.uuid }))
    });

    // Buckets: time-related (per date), or a single bucket from the total quantity
    const useDateBuckets = Array.isArray(desiredQuantityOrDateBuckets) && desiredQuantityOrDateBuckets.length > 0;
    let buckets: DemandDateBucket[];
    if (useDateBuckets) {
      buckets = (desiredQuantityOrDateBuckets as DemandDateBucket[]).filter(b => b.quantity > 0);
    } else {
      const singleQty = typeof desiredQuantityOrDateBuckets === 'number' ? desiredQuantityOrDateBuckets : 0;
      const planningStart = formData.plannedStart?.split('T')[0];
      const planningEnd = formData.plannedEnd?.split('T')[0];
      const inPlanningHorizon = (d: DemandDto) => {
        const dDate = d.demandDate?.split('T')[0];
        return dDate && planningStart && planningEnd && dDate >= planningStart && dDate <= planningEnd;
      };
      const demandsForMaterial = demands.filter(d => d.materialId === materialId && d.demandDate);
      const demandInHorizon = demandsForMaterial.filter(inPlanningHorizon).sort((a, b) => (a.demandDate || '').localeCompare(b.demandDate || ''))[0];
      const demandForMaterial = demandInHorizon ?? demandsForMaterial[0];
      const demandDateStr = demandForMaterial?.demandDate?.includes('T') ? demandForMaterial.demandDate : (demandForMaterial?.demandDate ? `${demandForMaterial.demandDate}T00:00:00` : '');
      const dueTimeStr = (demandForMaterial?.dueTime || '00:00').length === 5 ? (demandForMaterial?.dueTime + ':00') : (demandForMaterial?.dueTime || '00:00:00');
      buckets = singleQty > 0 ? [{ demandDate: demandDateStr || '', quantity: singleQty, dueTime: dueTimeStr }] : [];
    }
    if (buckets.length === 0) {
      setBatchDeterminationData(materialId, {
        materialId,
        materialName: material.name || '',
        batchDetermination: 'FULL_OVEN',
        desiredQuantity: 0,
        bakedQuantity: 0,
        doughQuantity: 0,
        doughBatches: [],
        ovenBatches: [],
        campaigns: [],
        doughBatchMethod: 'even'
      });
      return;
    }

    // Set the preview UUID once (used for the campaign id preview and sent to the backend on save)
    if (!previewScheduleUuidRef.current) {
      previewScheduleUuidRef.current = generateUUID();
    }

    // Campaign id preview in the format yyyyMMdd_<schedulePrefix>_<productSlug>_<seq> (as in the backend CampaignService)
    const CAMPAIGN_ID_SLUG_MAX_LENGTH = 30;
    const toProductSlug = (name: string, materialIdFallback: string): string => {
      if (name == null || !name.trim()) {
        return materialIdFallback.replace(/-/g, '').substring(0, 8);
      }
      let slug = name.trim().replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      if (slug.length === 0) {
        return materialIdFallback.replace(/-/g, '').substring(0, 8);
      }
      if (slug.length > CAMPAIGN_ID_SLUG_MAX_LENGTH) {
        slug = slug.substring(0, CAMPAIGN_ID_SLUG_MAX_LENGTH);
      }
      return slug;
    };
    const generateCampaignIdPreview = (materialName: string, seq: number): string => {
      const plannedStart = formData.plannedStart;
      const dateStr = plannedStart
        ? plannedStart.replace(/-/g, '').substring(0, 8)
        : new Date().toISOString().replace(/-/g, '').substring(0, 8);
      const schedulePrefix = (previewScheduleUuidRef.current ?? '').replace(/-/g, '').substring(0, 8);
      const slug = toProductSlug(materialName, materialId);
      return `${dateStr}_${schedulePrefix}_${slug}_${seq}`;
    };

    // Get override or use product defaults
    const override = batchDeterminationOverrides[materialId];
    const batchDetermination = override?.batchDetermination || material.ovenBatchDetermination || material.batchDetermination || 'FULL_OVEN';
    const minOvenUtilizationPercent = override?.minOvenUtilizationPercent || material.minOvenUtilizationPercent;
    // An explicitly passed method beats a stored override, which beats the material default.
    // (The order matters because of a stale closure when switching the radio - see the helper documentation.)
    const effectiveDoughBatchMethod = resolveEffectiveDoughBatchMethod(
      doughBatchMethod,
      override?.doughBatchMethod,
      material.doughBatchMethod
    );

    // Phase 1: Only FULL_OVEN and OVEN_UTILIZATION are supported
    let effectiveBatchDetermination = batchDetermination;
    if (batchDetermination !== 'FULL_OVEN' && batchDetermination !== 'OVEN_UTILIZATION') {
      console.warn('Unsupported batchDetermination type in Phase 1:', batchDetermination, '- falling back to FULL_OVEN');
      effectiveBatchDetermination = 'FULL_OVEN';
    }

    const scrapRate = (material as any).scrapRate ?? 0.02;
    const allCampaigns: Campaign[] = [];
    let totalDesiredQuantity = 0;
    let totalBakedQuantity = 0;
    let totalDoughQuantity = 0;
    const allDoughBatches: DoughBatch[] = [];
    const allOvenBatches: OvenBatch[] = [];
    let lastAssignmentWarning: BatchAssignmentWarning | undefined;

    // Find all sequence entries for this product (once, used for every bucket)
    const productSequenceEntries = sequenceEntries
      .filter(entry => entry.productId === materialId)
      .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));

    const calculateMaxAssignableBatches = (): number | null => {
      if (productSequenceEntries.length === 0) return null;
      const lastEntry = productSequenceEntries[productSequenceEntries.length - 1];
      if (lastEntry.maxBatches === null || lastEntry.maxBatches === undefined) return null;
      let sum = 0;
      for (const entry of productSequenceEntries) {
        if (entry.maxBatches != null) sum += entry.maxBatches;
        else return null;
      }
      return sum;
    };

    logWizardBatchQty('calc.preLoop', {
      materialId,
      bucketsCount: buckets.length,
      productSequenceEntries: productSequenceEntries.map(e => ({ seq: e.sequenceNumber, productId: e.productId, maxBatches: e.maxBatches })),
      maxAssignableBatches: calculateMaxAssignableBatches()
    });

    for (const bucket of buckets) {
      const dueDateISO: string | undefined = bucket.demandDate
        ? formatDemandDueDateAsIsoOffset(bucket.demandDate, bucket.dueTime)
        : undefined;
      const desiredQuantity = bucket.quantity + Math.round(bucket.quantity * scrapRate);

      let bakedQuantity: number;
      let doughQuantity: number;
      let doughBatches: DoughBatch[];
      let ovenBatches: OvenBatch[] | undefined;

      try {
        const serviceResult = calculateBatchesFromService({
          desiredQuantity,
          batchDetermination: effectiveBatchDetermination,
          minOvenUtilizationPercent: minOvenUtilizationPercent ?? 60,
          doughBatchMethod: effectiveDoughBatchMethod,
          material: {
            piecesPerOven: material.piecesPerOven || 100,
            initialWeighInGrams: material.initialWeighInGrams || 250,
            minDoughQuantityInKg: material.minDoughQuantityInKg || 0,
            maxDoughQuantityInKg: material.maxDoughQuantityInKg || 250
          },
          fixedDoughBatches: fixedDoughBatches?.map(b => ({
            sequenceNumber: b.sequenceNumber,
            quantity: b.quantity,
            doughQuantityKg: b.doughQuantityKg,
            uuid: b.uuid
          }))
        });
        bakedQuantity = serviceResult.bakedQuantity;
        doughQuantity = serviceResult.doughQuantity;
        doughBatches = serviceResult.doughBatches as DoughBatch[];
        ovenBatches = serviceResult.ovenBatches as OvenBatch[];
        logWizardBatchQty('calc.serviceResult', {
          materialId,
          bucketQty: bucket.quantity,
          desiredQuantity,
          bakedQuantity,
          doughQuantity,
          doughBatchesCount: doughBatches.length,
          doughBatches: doughBatches.map(b => ({ seq: b.sequenceNumber, qty: b.quantity, kg: b.doughQuantityKg, uuid: b.uuid })),
          ovenBatchesCount: ovenBatches?.length ?? 0
        });
      } catch (err) {
        if (err instanceof FixedDoughBatchesValidationError) {
          handleError(err, 'Validierung fixer Teig-Chargen');
        } else {
          throw err;
        }
        return;
      }

      totalDesiredQuantity += bucket.quantity;
      totalBakedQuantity += bakedQuantity;
      totalDoughQuantity += doughQuantity;
      allDoughBatches.push(...doughBatches);
      if (ovenBatches) allOvenBatches.push(...ovenBatches);

      // Step 5: assign campaigns from the sequence (per bucket)
      const campaigns: Campaign[] = [];
      let campaignCounter = allCampaigns.length + 1;
      let isFirstCampaignOfBucket = true;
      let currentSequenceEntryIndex = 0;
      let currentSequenceEntry: CampaignSequenceEntryDto | null = null;
      let currentCampaignDoughBatches: DoughBatch[] = [];
      let batchesUsedInCurrentEntry = 0;
      let assignedBatchesCount = 0;

      const maxAssignableBatches = calculateMaxAssignableBatches();

      if (productSequenceEntries.length === 0) {
        campaigns.push({
          campaignId: generateCampaignIdPreview(material.name || '', campaignCounter),
          sequenceNumber: 0,
          materialId: materialId,
          materialName: material.name || '',
          desiredQuantity: desiredQuantity,
          totalBakedQuantity: bakedQuantity,
          dueDate: dueDateISO,
          doughBatches: [...doughBatches],
          ovenBatches: ovenBatches
        });
        assignedBatchesCount = doughBatches.length;
      } else {
      // Distribute batches across sequence entries
      for (const doughBatch of doughBatches) {
        // Check whether we can still assign batches
        if (maxAssignableBatches !== null && assignedBatchesCount >= maxAssignableBatches) {
          // No further assignment possible - the batch stays unassigned
          // The warning is created later
          break;
        }

        // Find next sequence entry for this product
        if (!currentSequenceEntry || 
            (currentSequenceEntry.maxBatches !== null && 
             currentSequenceEntry.maxBatches !== undefined &&
             batchesUsedInCurrentEntry >= currentSequenceEntry.maxBatches)) {
          // Move to next sequence entry (only increment when we had a previous entry; otherwise start at 0)
          if (currentSequenceEntry != null) {
            currentSequenceEntryIndex++;
          }
          if (currentSequenceEntryIndex >= productSequenceEntries.length) {
            // Use last entry (unlimited or already at max)
            currentSequenceEntry = productSequenceEntries[productSequenceEntries.length - 1];
            // Check whether the last entry still has capacity
            if (currentSequenceEntry.maxBatches !== null && 
                currentSequenceEntry.maxBatches !== undefined &&
                maxAssignableBatches !== null &&
                assignedBatchesCount >= maxAssignableBatches) {
              // No further assignment possible
              break;
            }
          } else {
            currentSequenceEntry = productSequenceEntries[currentSequenceEntryIndex];
          }
          batchesUsedInCurrentEntry = 0;

          // Start new campaign if we have batches from previous entry
          if (currentCampaignDoughBatches.length > 0) {
            const previousEntry = productSequenceEntries[currentSequenceEntryIndex - 1];
            // IMPORTANT: the oven batches belong to all campaigns, because they were computed for the whole bakedQuantity
            // All oven batches are assigned to the campaign
            campaigns.push({
              campaignId: generateCampaignIdPreview(material.name || '', campaignCounter),
              sequenceNumber: previousEntry.sequenceNumber || 0,
              materialId: materialId,
              materialName: material.name || '',
              desiredQuantity: isFirstCampaignOfBucket ? desiredQuantity : 0,
              totalBakedQuantity: bakedQuantity,
              dueDate: dueDateISO,
              doughBatches: [...currentCampaignDoughBatches],
              ovenBatches: ovenBatches ? [...ovenBatches] : undefined
            });
            isFirstCampaignOfBucket = false;
            campaignCounter++;
            currentCampaignDoughBatches = [];
          }
        }

        currentCampaignDoughBatches.push(doughBatch);
        batchesUsedInCurrentEntry++;
        assignedBatchesCount++;

        if (currentSequenceEntry.maxBatches !== null &&
            currentSequenceEntry.maxBatches !== undefined &&
            batchesUsedInCurrentEntry >= currentSequenceEntry.maxBatches) {
          campaigns.push({
            campaignId: generateCampaignIdPreview(material.name || '', campaignCounter),
            sequenceNumber: currentSequenceEntry.sequenceNumber || 0,
            materialId: materialId,
            materialName: material.name || '',
            desiredQuantity: isFirstCampaignOfBucket ? desiredQuantity : 0,
            totalBakedQuantity: bakedQuantity,
            dueDate: dueDateISO,
            doughBatches: [...currentCampaignDoughBatches],
            ovenBatches: ovenBatches ? [...ovenBatches] : undefined
          });
          isFirstCampaignOfBucket = false;
          campaignCounter++;
          currentCampaignDoughBatches = [];
        }
      }

      if (currentCampaignDoughBatches.length > 0 && currentSequenceEntry) {
        const canAdd = currentSequenceEntry.maxBatches === null ||
                      currentSequenceEntry.maxBatches === undefined ||
                      batchesUsedInCurrentEntry < currentSequenceEntry.maxBatches;

        if (canAdd && (maxAssignableBatches === null || assignedBatchesCount < maxAssignableBatches)) {
          campaigns.push({
            campaignId: generateCampaignIdPreview(material.name || '', campaignCounter),
            sequenceNumber: currentSequenceEntry.sequenceNumber || 0,
            materialId: materialId,
            materialName: material.name || '',
            desiredQuantity: isFirstCampaignOfBucket ? desiredQuantity : 0,
            totalBakedQuantity: bakedQuantity,
            dueDate: dueDateISO,
            doughBatches: [...currentCampaignDoughBatches],
            ovenBatches: ovenBatches ? [...ovenBatches] : undefined
          });
        }
      }
      }

      // Warning for this bucket
      if (assignedBatchesCount < doughBatches.length) {
        lastAssignmentWarning = {
          unassignedBatches: doughBatches.length - assignedBatchesCount,
          totalBatches: doughBatches.length,
          assignedBatches: assignedBatchesCount,
          maxBatchesSum: maxAssignableBatches
        };
      }
      allCampaigns.push(...campaigns);
    }

    logWizardBatchQty('calc.afterDistribute', {
      materialId,
      campaignsCount: allCampaigns.length,
      campaigns: allCampaigns.map(c => ({
        campaignId: c.campaignId,
        sequenceNumber: c.sequenceNumber,
        desiredQuantity: c.desiredQuantity,
        totalBakedQuantity: c.totalBakedQuantity,
        doughBatchesCount: c.doughBatches?.length ?? 0,
        doughBatchesSeqs: (c.doughBatches ?? []).map(b => b.sequenceNumber),
        ovenBatchesCount: c.ovenBatches?.length ?? 0
      }))
    });

    // Per campaign: recompute the oven batches and split them when needed.
    // The logic lives in rebuildCampaignsWithSplitting (campaignRebuildService.ts, tested there too).
    const availableOvens = availableOvensByMaterialRef.current[materialId];
    if (campaignSplittingEnabledRef.current && availableOvens && availableOvens > 0) {
      const fixedBatchUuids = new Set(
        (fixedDoughBatches ?? []).map(b => b.uuid).filter((u): u is string => u != null)
      );
      const rebuilt = rebuildCampaignsWithSplitting({
        campaigns: allCampaigns,
        fixedDoughBatchUuids: fixedBatchUuids,
        productSequenceEntries,
        availableOvens,
        materialParams: {
          piecesPerOven: material.piecesPerOven || 100,
          initialWeighInGrams: material.initialWeighInGrams || 250,
          minDoughQuantityInKg: material.minDoughQuantityInKg || 0,
          maxDoughQuantityInKg: material.maxDoughQuantityInKg || 250
        },
        batchDetermination: effectiveBatchDetermination,
        minOvenUtilizationPercent: minOvenUtilizationPercent ?? 60,
        doughBatchMethod: effectiveDoughBatchMethod,
        generateCampaignId: (seq) => generateCampaignIdPreview(material.name || '', seq)
      });
      logWizardBatchQty('calc.rebuildSplitting', {
        materialId,
        availableOvens,
        fixedBatchUuids: Array.from(fixedBatchUuids),
        beforeCount: allCampaigns.length,
        afterCount: rebuilt.length,
        rebuilt: rebuilt.map(c => ({
          campaignId: c.campaignId,
          sequenceNumber: c.sequenceNumber,
          desiredQuantity: c.desiredQuantity,
          totalBakedQuantity: c.totalBakedQuantity,
          doughBatchesCount: c.doughBatches?.length ?? 0,
          doughBatchesSeqs: (c.doughBatches ?? []).map(b => b.sequenceNumber),
          ovenBatchesCount: c.ovenBatches?.length ?? 0
        }))
      });
      allCampaigns.splice(0, allCampaigns.length, ...rebuilt);
    }

    // Check the campaigns for remaining oven overcapacity after the rebuild.
    const effectiveAvailableOvens = availableOvensByMaterialRef.current[materialId] ?? 0;
    const ovenCapacityWarnings = effectiveAvailableOvens > 0
      ? allCampaigns
          .filter(c => (c.ovenBatches?.length ?? 0) > effectiveAvailableOvens)
          .map(c => ({ campaignId: c.campaignId, ovenBatches: c.ovenBatches!.length, availableOvens: effectiveAvailableOvens }))
      : undefined;

    // Create the WizardBatchData (merged from all buckets)
    const batchData: WizardBatchData = {
      materialId,
      materialName: material.name || '',
      batchDetermination: effectiveBatchDetermination,
      minOvenUtilizationPercent: effectiveBatchDetermination === 'OVEN_UTILIZATION' ? minOvenUtilizationPercent : undefined,
      desiredQuantity: totalDesiredQuantity,
      bakedQuantity: totalBakedQuantity,
      doughQuantity: totalDoughQuantity,
      doughBatches: allDoughBatches,
      ovenBatches: allOvenBatches.length > 0 ? allOvenBatches : undefined,
      campaigns: allCampaigns,
      doughBatchMethod: effectiveDoughBatchMethod,
      assignmentWarning: lastAssignmentWarning,
      ovenCapacityWarnings: ovenCapacityWarnings && ovenCapacityWarnings.length > 0 ? ovenCapacityWarnings : undefined
    };

    setBatchDeterminationData(materialId, batchData);
  }, [demands, materials, batchDeterminationOverrides, sequenceEntries, setBatchDeterminationData, formData.plannedStart, formData.plannedEnd, handleError, planningMode, fixedDoughBatchesByMaterial]);

  // Automatically adjust the batch count when not all batches could be assigned
  const adjustBatchCountForSequence = useCallback((materialId: string) => {
    const currentData = batchDeterminationData[materialId];
    if (!currentData || !currentData.assignmentWarning) {
      return; // no warning present
    }

    const material = materials.find(m => m.uuid === materialId);
    if (!material) {
      console.warn('Material not found for batch adjustment:', materialId);
      return;
    }

    const warning = currentData.assignmentWarning;
    const maxAssignableBatches = warning.maxBatchesSum;
    
    if (maxAssignableBatches === null) {
      // Unlimited - should not happen, but just in case
      return;
    }

    // Compute the new batch count: at least as many batches as maxAssignableBatches,
    // but respect minDoughQuantityInKg
    const minDoughQuantityInKg = material.minDoughQuantityInKg || 0;
    const maxDoughQuantityInKg = material.maxDoughQuantityInKg || 250;
    const doughQuantity = currentData.doughQuantity;
    
    // New count: at least maxAssignableBatches, but such that every batch is >= minDoughQuantityInKg
    let newBatchCount = Math.max(maxAssignableBatches, Math.ceil(doughQuantity / maxDoughQuantityInKg));
    
    // Check whether every batch has at least minDoughQuantityInKg
    const weightPerBatch = doughQuantity / newBatchCount;
    if (weightPerBatch < minDoughQuantityInKg) {
      // Reduce the count so that every batch is >= minDoughQuantityInKg
      newBatchCount = Math.floor(doughQuantity / minDoughQuantityInKg);
      if (newBatchCount === 0) {
        newBatchCount = 1; // at least 1 batch
      }
    }

    // Recompute the batches with the new count
    const weightPerBatchNew = doughQuantity / newBatchCount;
    const initialWeighInGrams = material.initialWeighInGrams || 250;
    const piecesPerBatchNew = Math.round((weightPerBatchNew * 1000) / initialWeighInGrams);

    const newDoughBatches: DoughBatch[] = [];
    for (let i = 0; i < newBatchCount; i++) {
      newDoughBatches.push({
        id: `dough-${materialId}-${i + 1}`,
        sequenceNumber: i + 1,
        quantity: piecesPerBatchNew,
        weight: weightPerBatchNew,
        doughQuantityKg: weightPerBatchNew,
        materialId,
        materialName: material.name || ''
      });
    }

    // Recompute with the adjusted batches
    const defaultMethod = material.doughBatchMethod === 'FULL' ? 'full' : 'even';
    const doughBatchMethod = currentData.doughBatchMethod || defaultMethod;
    calculateBatchDetermination(materialId, currentData.desiredQuantity, doughBatchMethod);
  }, [batchDeterminationData, materials, calculateBatchDetermination]);

  const startPlanningWithDefaults = useCallback(async (): Promise<boolean> => {
    try {
      const flushResult = step1FlushRef.current?.() ?? { saves: [], clears: [] };
      const demandsForPlanning = await savePendingStep1Demands(flushResult);
      const demandsToUse = demandsForPlanning ?? displayEffectiveDemands;

      const plannedStart = formData.plannedStart;
      const startDate = plannedStart ? new Date(plannedStart) : new Date();
      const dayIndex = startDate.getDay();
      const dayNames = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
      const dayOfWeek = dayNames[dayIndex];

      const defaultSeq = await CampaignSequenceControllerService.getCampaignSequenceByDayOfWeek(dayOfWeek).catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      });

      const entries = defaultSeq?.entries ?? [];
      setSelectedDayOfWeek(dayOfWeek);
      setSequenceEntries(entries);
      setAvailableSequencesForDay(defaultSeq ? [{ uuid: defaultSeq.uuid, name: defaultSeq.name, isDefault: true }] : []);
      setAutoAddedProductIds(new Set());

      const productIdsFromDemands = new Set<string>();
      demandsToUse.forEach((d: DemandDto) => {
        if (!d.materialId) return;
        const material = materials.find((m: { uuid?: string }) => m.uuid === d.materialId);
        if (!material || material.materialType !== 'PRODUCT') return;
        const effectiveId = getEffectiveMaterialId(d.materialId, materials);
        if (effectiveId) productIdsFromDemands.add(effectiveId);
      });

      const productIdsInSequence = new Set(
        entries
          .filter((e): e is CampaignSequenceEntryDto & { productId: string } => typeof e.productId === 'string')
          .map((e) => e.productId)
      );
      const missingProductIds = Array.from(productIdsFromDemands).filter((id: string) => !productIdsInSequence.has(id));

      if (missingProductIds.length > 0) {
        const maxSeq = entries.length > 0 ? Math.max(...entries.map((e: { sequenceNumber?: number }) => e.sequenceNumber || 0)) : 0;
        const newEntries = missingProductIds.map((productId: string, i: number) => {
          const material = materials.find((m: { uuid?: string }) => m.uuid === productId);
          return {
            uuid: `temp-${productId}-${i}`,
            productId,
            productName: material?.name ?? '',
            sequenceNumber: maxSeq + i + 1
          };
        });
        setSequenceEntries((prev: CampaignSequenceEntryDto[]) => [...prev, ...newEntries]);
      }

      await new Promise(r => setTimeout(r, 0));

      const qtyByMaterial = new Map<string, number>();
      demandsToUse.forEach((d: DemandDto) => {
        if (!d.materialId) return;
        const effectiveId = getEffectiveMaterialId(d.materialId, materials);
        if (!effectiveId) return;
        const material = materials.find((m: { uuid?: string }) => m.uuid === effectiveId);
        if (!material || material.materialType !== 'PRODUCT') return;
        const qty = (d.quantity ?? 0) + (qtyByMaterial.get(effectiveId) ?? 0);
        qtyByMaterial.set(effectiveId, qty);
      });

      // Time-related: use the buckets when present, otherwise the total quantity (legacy)
      productIdsFromDemands.forEach((materialId: string) => {
        const buckets = demandBucketsByProduct?.[materialId];
        if (buckets?.length > 0) {
          calculateBatchDetermination(materialId, buckets);
        } else {
          const qty = qtyByMaterial.get(materialId) ?? 0;
          if (qty > 0) calculateBatchDetermination(materialId, qty);
        }
      });

      await new Promise(r => setTimeout(r, 0));

      const step3Validation = validateStep3();
      if (!step3Validation.isValid) {
        showToast('info', t('productionSchedule.wizard.step3.validation.noBatches', 'Keine Chargen'),
          t('productionSchedule.wizard.step3.validation.noBatchesOrAusbackenMessage', 'Bitte mindestens eine Charge oder Ausbacken-Aufträge für diesen Plan.'));
        return false;
      }

      const routingIssues = await validateRoutingsForStep3();
      if (routingIssues.length > 0) {
        setRoutingValidationIssues(routingIssues);
        showToast('info', t('productionSchedule.wizard.step3.validation.title', 'Validierungsfehler'),
          t('productionSchedule.wizard.step3.validation.message', 'Für einige Kampagnen wurde kein gültiger Arbeitsplan gefunden. Bitte gehen Sie die Schritte 2 und 3 durch.'));
        return false;
      }

      setRoutingValidationIssues(null);
      await directStartSaveRef.current?.();
      return true;
    } catch (err) {
      handleError(err as Error, 'Direktstart');
      return false;
    }
  }, [savePendingStep1Demands, formData.plannedStart, displayEffectiveDemands, demandBucketsByProduct, materials, setSelectedDayOfWeek, setSequenceEntries, setAvailableSequencesForDay, setAutoAddedProductIds, calculateBatchDetermination, validateStep3, validateRoutingsForStep3, setRoutingValidationIssues, showToast, t, handleError]);

  // Memoize context value to prevent unnecessary re-renders and ensure stability during hot reloads
  const contextValue: WizardState & WizardActions = useMemo(() => ({
    // State
    formData,
    planningSettings,
    isLoadingSettings,
    demands,
    orders,
    forecasts,
    effectiveDemands,
    productionEffectiveDemands,
    productionDisplayDemands,
    productionDisplayDemandsAggregated,
    demandBucketsByProduct,
    pendingOrders,
    pendingForecasts,
    pendingDeletions,
    materials,
    isLoadingDemands,
    selectedMaterialIndex,
    batches,
    localBatchesMap,
    batchDeterminationData,
    batchDeterminationOverrides,
    productionOrders,
    isCreatingProductionOrders,
    productionOrderCreationProgress,
    step3ValidationError,
    routingValidationIssues,
    materialIdsQuantityLocked,
    sequenceOptimization,
    selectedDayOfWeek,
    selectedSequenceUuid,
    availableSequencesForDay,
    sequenceEntries,
    autoAddedProductIds,
    planningMode,
    sourceScheduleUuid,
    sourceSchedulePlannedStart,
    frozenZoneEnd,
    fixedDoughBatchesByMaterial,
    step0ReleasedPlansLoading,
    step0HasReleasedPlans,
    step1ReplanningHasNewSollBelowIst,
    lockedReplanningMaterialIds,
    hasFollowUpOrders,
    followUpOrders,
    currentStep,
    settingsLoadedRef,

    // Actions
    updateFormData,
    setPlanningMode,
    setSourceScheduleUuid,
    setSourceSchedulePlannedStart,
    setFrozenZoneEnd,
    setStep0ReleasedPlansState,
    setStep1ReplanningHasNewSollBelowIst,
    setLockedReplanningMaterialIds,
    setCurrentStep,
    handleNext,
    setSequenceOptimization,
    setSelectedDayOfWeek,
    setSelectedSequenceUuid,
    setAvailableSequencesForDay,
    setSequenceEntries,
    addSequenceEntry,
    updateSequenceEntry,
    deleteSequenceEntry,
    setAutoAddedProductIds,
    validateSequenceEntries,
    getPreviewScheduleUuid: () => previewScheduleUuidRef.current,
    handlePrevious,
    setSelectedMaterialIndex,
    setBatches,
    updateBatch,
    updateBatchesForMaterial,
    setLocalBatchesMap,
    setBatchDeterminationData,
    setBatchDeterminationOverride,
    calculateBatchDetermination,
    adjustBatchCountForSequence,
    removeBatchDeterminationData,
    setCampaignsWithoutRoutingToZero,
    createProductionOrders,
    deleteProductionOrders,
    validateStep3,
    clearStep3ValidationError,
    loadDemands,
    loadMaterials,
    addOrUpdatePendingOrder,
    addOrUpdatePendingForecast,
    clearPendingDemandCell,
    addPendingOrdersFromImport,
    addPendingForecastsFromImport,
    addPendingDeletion,
    savePendingStep1Demands,
    generateDefaultScheduleName,
    convertDemandsForView,
    formatDate,
    calculateBatches,
    registerDirectStartSave,
    startPlanningWithDefaults,
    registerStep1FlushPendingEdits,
    registerStep2aIntermediateFlush,
    registerStep2aIntermediateCleanup,
    pendingIntermediateOrders,
    setPendingIntermediateOrders
  }), [
    formData,
    planningSettings,
    isLoadingSettings,
    demands,
    orders,
    forecasts,
    effectiveDemands,
    productionEffectiveDemands,
    productionDisplayDemands,
    productionDisplayDemandsAggregated,
    demandBucketsByProduct,
    pendingOrders,
    pendingForecasts,
    pendingDeletions,
    materials,
    isLoadingDemands,
    selectedMaterialIndex,
    batches,
    localBatchesMap,
    batchDeterminationData,
    batchDeterminationOverrides,
    productionOrders,
    isCreatingProductionOrders,
    productionOrderCreationProgress,
    step3ValidationError,
    routingValidationIssues,
    materialIdsQuantityLocked,
    sequenceOptimization,
    selectedDayOfWeek,
    selectedSequenceUuid,
    availableSequencesForDay,
    sequenceEntries,
    autoAddedProductIds,
    planningMode,
    sourceScheduleUuid,
    sourceSchedulePlannedStart,
    frozenZoneEnd,
    fixedDoughBatchesByMaterial,
    step0ReleasedPlansLoading,
    step0HasReleasedPlans,
    step1ReplanningHasNewSollBelowIst,
    lockedReplanningMaterialIds,
    hasFollowUpOrders,
    followUpOrders,
    currentStep,
    settingsLoadedRef,
    updateFormData,
    setPlanningMode,
    setSourceScheduleUuid,
    setSourceSchedulePlannedStart,
    setFrozenZoneEnd,
    setStep0ReleasedPlansState,
    setStep1ReplanningHasNewSollBelowIst,
    setLockedReplanningMaterialIds,
    setCurrentStep,
    handleNext,
    handlePrevious,
    setSequenceOptimization,
    setSelectedDayOfWeek,
    setSelectedSequenceUuid,
    setAvailableSequencesForDay,
    setSequenceEntries,
    addSequenceEntry,
    updateSequenceEntry,
    deleteSequenceEntry,
    setAutoAddedProductIds,
    validateSequenceEntries,
    setSelectedMaterialIndex,
    setBatches,
    updateBatch,
    updateBatchesForMaterial,
    setLocalBatchesMap,
    setBatchDeterminationData,
    setBatchDeterminationOverride,
    calculateBatchDetermination,
    adjustBatchCountForSequence,
    createProductionOrders,
    deleteProductionOrders,
    validateStep3,
    clearStep3ValidationError,
    loadDemands,
    loadMaterials,
    addOrUpdatePendingOrder,
    addOrUpdatePendingForecast,
    clearPendingDemandCell,
    addPendingOrdersFromImport,
    addPendingForecastsFromImport,
    addPendingDeletion,
    savePendingStep1Demands,
    generateDefaultScheduleName,
    convertDemandsForView,
    formatDate,
    calculateBatches,
    registerDirectStartSave,
    startPlanningWithDefaults,
    registerStep1FlushPendingEdits,
    registerStep2aIntermediateFlush,
    registerStep2aIntermediateCleanup,
    pendingIntermediateOrders,
    setPendingIntermediateOrders,
    removeBatchDeterminationData,
    setCampaignsWithoutRoutingToZero
  ]);

  return (
    <WizardContext.Provider value={contextValue}>
      {children}
    </WizardContext.Provider>
  );
};
